/* i18n bridge, from snippets/i18n-js.liquid. Redefining it here when it is
   absent keeps every call site working in English rather than throwing. */
window.BT_T = window.BT_T || function (key, fallback) { return fallback; };

// Define a convenience method and use it like
// ready(() => {...});
var ready = (callback) => {
  if (document.readyState != 'loading') callback();
  else document.addEventListener('DOMContentLoaded', callback);
};

/*
 * MONEY + ESCAPING
 * One formatter for the whole theme, defined here because theme.js is the
 * first script the layout loads.
 *
 * There were three before, and they disagreed. wishlist.js honoured
 * Shopify.money_format; the cart drawer preferred Shopify.formatMoney and fell
 * back to a hardcoded '$' + toFixed(2); cart.js called Shopify.formatMoney with
 * no fallback at all. Shopify.formatMoney ships with option_selection.js, which
 * this theme does not load, so it is undefined here: the drawer always took its
 * fallback, and cart.js threw. This shop's format is ${{amount_no_decimals}},
 * so the drawer was also showing $280.00 where the rest of the store says $280.
 */
window.BTFormat = (function () {
  function money(cents) {
    var fmt = (window.Shopify && Shopify.money_format) || '${{amount}}';
    var amount = (Number(cents) / 100).toFixed(2);
    var whole = amount.split('.')[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // no_decimals ROUNDS, as Liquid's money filter does: $18.90 is "$19" on
    // the cart page and was "$18" here, because `whole` is the truncated
    // integer part. Found beside a 10% discount line on 2026-09-03.
    var rounded = String(Math.round(Number(cents) / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace('{{amount}}', whole + '.' + amount.split('.')[1])
      .replace('{{amount_no_decimals}}', rounded)
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
      .replace(/<\/?[^>]+>/g, '');
  }

  // Product titles land in HTML strings and attributes: `Meg 54" Cap Sleeve
  // Caftan` is in this catalogue, and that quote closes an attribute early.
  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Colour names in Title Case: each word's first letter up, the rest as
  // entered, so "pool" reads "Pool" and a "USA" or "McKenzie" keeps its
  // capitals (the user, 2026-10-08; sentence case, "Santorini blue", was the
  // house style before). The JS twin of snippets/title-case.liquid, for what
  // is built in JS and cannot reach Liquid. Words split on spaces only; no
  // colour in the catalogue has a hyphen or a slash.
  //
  // Done on the value rather than with CSS text-transform: capitalize, which
  // cannot reach a title or aria-label attribute.
  function title(str) {
    return String(str == null ? '' : str).trim().split(/\s+/).map(function (w) {
      return w.charAt(0).toUpperCase() + w.slice(1);
    }).join(' ');
  }

  return { money: money, esc: esc, title: title };
})();

/*
 * THE CART
 * One way to change the cart, for every surface that shows it: the drawer,
 * the cart page, quick add and the product form.
 *
 * Each of those used to call the Cart API on its own (Codex review,
 * 2026-10-01). The cart page patched the row it touched and nothing else, and
 * the drawer kept the snapshot it fetched at page load, so a quantity changed
 * on /cart left the drawer stale behind it, and a discount that moved two
 * lines updated one of them.
 *
 * Now every change made from this page runs here (other tabs and apps are
 * outside its reach), one at a time and in order, and ends with
 * the cart as the server confirms it, announced on document as `bt:cart`
 * with { cart, sections }. Surfaces re-render from that rather than patching
 * themselves. A surface rendered by Liquid registers its section id, and
 * Shopify returns that section's HTML in the same response (bundled section
 * rendering), so the page and the JSON describe one cart.
 */
window.BTCart = (function () {
  /* Long enough for a slow phone connection; short enough that one request
     which never answers cannot hold every later change behind it. It runs
     until the BODY has been read, not just the headers: a response whose
     body stalled used to clear the timer and hold the queue for good (Codex,
     fourth pass). A timeout is an UNKNOWN outcome like any other.

     WHAT A TIMEOUT CANNOT DO. Aborting the request does not stop the server.
     A change can still be applying when the client gives up, so the read
     that follows can show the cart from before it, and the change can land
     after a later one. Nothing here claims otherwise: the shopper is told
     the change could not be confirmed, nothing is replayed, and err.reconciled
     means only that a cart was read, not that the earlier change has
     finished. window.BTCartTimeout overrides the figure, for tests. */
  function timeout() { return window.BTCartTimeout || 30000; }
  var sections = [];
  var queue = Promise.resolve();
  var last = null;

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function t(key, fallback) { return window.BT_T ? window.BT_T(key, fallback) : fallback; }

  // One at a time: a second change waits for the first to settle, so the
  // responses land in the order the shopper made them.
  function enqueue(job) {
    var run = queue.then(job, job);
    queue = run.catch(function () {});
    return run;
  }

  /* TWO KINDS OF FAILURE, and the difference decides what the shopper is told.

     REFUSED: the server said no to what was asked. Only Shopify's own
     refusals count, a 422 (not enough stock, quantity rules) or a 404 (no
     such variant), each with its { description } body. A refusal is NOT
     proof that nothing changed: asked for 99 of a line with 5 in stock,
     Shopify sets the line to 5 and answers 422 "Only 5 items were added to
     your cart due to availability" (measured on the store, 2026-10-01). So
     a refusal is reconciled too.

     UNKNOWN: anything that does not prove a refusal -- a dropped connection,
     a timeout, a 5xx, a 429 or 400 (Shopify documents a 400 for a bad
     bundled-section parameter while the rest of the mutation went through),
     a 200 that is not JSON (an interstitial), or JSON that is not what the
     endpoint returns. The mutation may have happened. The service reads the
     real cart, publishes it, and rejects with err.unknown; nothing here ever
     retries, because a blind retry can double an add the server accepted.

     Codex's third pass (2026-10-01) found transport failures taking the
     refused path, so an add whose answer was lost looked refused and invited
     a second one. */
  function unknown(cause) {
    var err = new Error(t('cart_unconfirmed', 'We could not confirm that change. Please check your bag before trying again.'));
    err.unknown = true;
    err.cause = cause;
    return err;
  }

  function refused(data, status) {
    var err = new Error(data.description || data.message || t('cart_error', 'Sorry, we could not update your cart. Please try again.'));
    err.refused = true;
    err.status = status;
    return err;
  }

  function read(r) {
    return r.json().then(function (data) {
      if (r.ok) return data;
      if ((r.status === 422 || r.status === 404) && data && (data.description || data.message)) throw refused(data, r.status);
      throw unknown(new Error('HTTP ' + r.status));
    }, function () {
      throw unknown(new Error('Not JSON (HTTP ' + r.status + ')'));
    });
  }

  function send(path, opts) {
    var ctrl = new AbortController();
    var timer;
    var expired = new Promise(function (resolve, reject) {
      timer = setTimeout(function () {
        ctrl.abort();
        reject(unknown(new Error('Timed out after ' + timeout() + 'ms')));
      }, timeout());
    });
    opts.signal = ctrl.signal;
    var answered = fetch(root() + path, opts).then(read, function (e) { throw unknown(e); });
    return Promise.race([answered, expired]).then(function (data) {
      clearTimeout(timer);
      return data;
    }, function (err) {
      clearTimeout(timer);
      throw err;
    });
  }

  function post(path, body) {
    var opts = { method: 'POST', headers: { 'Accept': 'application/json' } };
    if (body instanceof FormData) {
      if (sections.length) body.set('sections', sections.join(','));
      opts.body = body;
    } else {
      body = Object.assign({}, body);
      if (sections.length) body.sections = sections.join(',');
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    return send(path, opts);
  }

  function isCart(c) { return !!c && Array.isArray(c.items) && typeof c.item_count === 'number'; }

  // A confirmed add is a line item -- a key, a quantity, a variant -- or
  // { items: [...] } of them.
  function isLine(i) { return !!i && typeof i.key === 'string' && typeof i.quantity === 'number' && i.variant_id != null; }
  function isAdded(a) {
    return !!a && (Array.isArray(a.items) ? a.items.length > 0 && a.items.every(isLine) : isLine(a));
  }

  function publish(cart, html) {
    last = cart;
    document.dispatchEvent(new CustomEvent('bt:cart', { detail: { cart: cart, sections: html || {} } }));
    return cart;
  }

  function readCart() {
    return send('cart.js', { headers: { 'Accept': 'application/json' } }).then(function (cart) {
      if (!isCart(cart)) throw unknown(new Error('Not a cart'));
      return cart;
    });
  }

  // After any failure, refused or unknown: show what the cart actually holds,
  // then reject with the ORIGINAL error, marked with whether the read worked.
  function reconcile(err) {
    return readCart().then(function (cart) {
      publish(cart);
      err.reconciled = true;
      throw err;
    }, function () {
      err.reconciled = false;
      throw err;
    });
  }

  function settle(err) { return reconcile(err || unknown(new Error('No error'))); }

  function refresh() {
    return enqueue(function () { return readCart().then(function (cart) { return publish(cart); }); });
  }

  /* A line's identity, which outlives its key. Shopify re-keys a line when a
     discount moves on it, so a key read before a change can be gone after
     it; variant, selling plan and properties together are what a line IS. */
  function identity(item) {
    var props = item.properties || {};
    var keys = Object.keys(props).sort();
    var plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
      ? item.selling_plan_allocation.selling_plan.id : '';
    return [item.variant_id || item.id, plan, JSON.stringify(keys.map(function (k) { return [k, props[k]]; }))].join('|');
  }

  /* Where an identity is in a cart, as one of four answers, kept apart:
       ok         one line; here is its key
       absent     the cart has no such line (it was removed)
       ambiguous  two lines share it; no key can be chosen safely
       nocart     there is no confirmed cart to ask yet
     Only `nocart` may fall back to another source, such as the render on
     screen. An absent or ambiguous answer from a confirmed cart is final:
     falling back past it revived stale keys (Codex, third pass). */
  function lookup(id, cart) {
    cart = cart || last;
    if (!cart) return { state: 'nocart' };
    var hits = cart.items.filter(function (i) { return identity(i) === id; });
    if (!hits.length) return { state: 'absent' };
    if (hits.length > 1) return { state: 'ambiguous' };
    return { state: 'ok', key: hits[0].key };
  }

  function keyFor(id, cart) {
    var l = lookup(id, cart);
    return l.state === 'ok' ? l.key : null;
  }

  /* /cart/change.js answers with the whole cart, so one round trip does it.
     params may be a function, called when this change's turn comes rather
     than when it was queued, so it can read the newest intent and key; a
     null from it means there is nothing to send, and the change resolves
     with null. */
  function change(params) {
    return enqueue(function () {
      var p = typeof params === 'function' ? params() : params;
      if (!p) return null;
      return post('cart/change.js', p).then(function (cart) {
        if (!isCart(cart)) throw unknown(new Error('Not a cart'));
        return publish(cart, cart.sections);
      }).catch(settle);
    });
  }

  /* /cart/add.js answers with the lines added, not the cart, so the cart is
     read after it. Three outcomes, kept apart:
       confirmed, cart read    resolves { added, cart }
       confirmed, read failed  resolves { added, cart: null }, and announces
                               `bt:cart-stale` for the drawer to explain
       refused or unknown      rejects (err.refused or err.unknown) */
  /* How many adds are queued or on their way. The newsletter popup holds
     off while one is: the bag is about to change, and the drawer may open.
     Counted here rather than read off a loading button, which a collection
     filter swap can remove mid-add (Codex REVIEW-5, 2026-11-02). */
  var adding = 0;

  function add(body) {
    adding++;
    var settled = function () { adding = Math.max(0, adding - 1); };
    var out = enqueue(function () {
      return post('cart/add.js', body).then(function (added) {
        if (!isAdded(added)) throw unknown(new Error('Not a confirmed add'));
        return readCart().then(function (cart) {
          publish(cart, added.sections);
          return { added: added, cart: cart };
        }, function () {
          document.dispatchEvent(new CustomEvent('bt:cart-stale'));
          return { added: added, cart: null };
        });
      }).catch(settle);
    });
    out.then(settled, settled);
    return out;
  }

  /* The cart note, through the same queue, so it cannot land between a change
     and the read that confirms it. /cart/update.js answers with the cart, and
     that is published like any change: the cart page renders the note too,
     and a clean note field there takes the saved one. */
  function update(params) {
    return enqueue(function () {
      return post('cart/update.js', params).then(function (cart) {
        if (!isCart(cart)) throw unknown(new Error('Not a cart'));
        return publish(cart, cart.sections);
      }).catch(settle);
    });
  }

  function register(id) { if (id && sections.indexOf(id) === -1) sections.push(id); }

  return {
    add: add, change: change, refresh: refresh, update: update, register: register,
    identity: identity, lookup: lookup, keyFor: keyFor,
    last: function () { return last; },
    adding: function () { return adding; }
  };
})();

/*
 * Set a <details> open or shut from code, through the slide in the accordion
 * block at the foot of this file: an animation still running would otherwise
 * finish afterwards and undo the change (Codex review, 2026-10-01: start
 * closing a store-locator region, pick it in the filter, and it ended shut).
 */
window.BTDetails = {
  set: function (d, open) {
    if (d._slide) { d._slide.cancel(); d._slide = null; }
    d.style.overflow = '';
    d.classList.remove('is-closing');
    d.open = !!open;
  }
};

/*
 * HISTORY STATE
 * Check if window.history is supported
 */
function historyState() {
  return window.history && window.history.replaceState;
}

/*
 * MODAL FOCUS + SCROLL LOCK
 * Shared by every dialog in the theme: the PDP lightbox, the size guide, the
 * header search panel, the cart drawer and the mobile filter sheet.
 *
 * Each of those already carried role="dialog" aria-modal="true", which tells a
 * screen reader the background is inert — but nothing made it so. Focus walked
 * straight out of the panel into the page behind, and closing dropped the
 * shopper back at the top of the document instead of at the control they came
 * from. This traps Tab inside the open panel and restores the trigger on close.
 *
 * Scroll locking is counted rather than set/unset, because five modals each
 * writing the lock meant closing one released it for any other still open.
 *
 * THE LOCK GOES ON documentElement, NOT body. On <body> it fixed the scroll
 * and moved the page: overflow:hidden makes body a scroll container, so a
 * position:sticky child resolves against body instead of the viewport, finds
 * a scrollTop of 0, and drops back to its static place. On the PDP that is the
 * whole buy column, measured jumping 176px up the moment the size drawer
 * opened, which read as the page behind the overlay lurching. On the root
 * element the value propagates to the viewport, the scroll position is kept,
 * sticky keeps resolving against the viewport, and the wheel is still dead.
 * Measured both ways on the PDP at scrollY 600: body moved .info from -246 to
 * -422, html held it at -246.
 *
 * Callers keep their own open/close classes; this only handles focus and scroll.
 * The chat widget manages its own focus already and is left alone.
 */
/*
 * NO TRANSITIONS WHILE RESIZING
 * A media query flipping is a change of declared values, and a transition
 * animates it exactly as it would a state change. Dragging the window past
 * 768px slid the mobile drawer into view and out again for about 11 frames.
 * The class is on the root; the rule that acts on it is in theme.css.
 */
(function () {
  var timer = null;
  window.addEventListener('resize', function () {
    document.documentElement.classList.add('bt-resizing');
    clearTimeout(timer);
    // Long enough to outlast the drawer's own .32s, so the suppressed
    // transition cannot restart into the tail of the resize.
    timer = setTimeout(function () {
      document.documentElement.classList.remove('bt-resizing');
    }, 400);
  }, {passive: true});
})();

window.BTModal = (function () {
  var stack = [];
  var locks = 0;
  var SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function focusable(root) {
    return Array.prototype.filter.call(root.querySelectorAll(SELECTOR), function (el) {
      // A link or button carrying tabindex="-1" is out of the Tab order on
      // purpose (the product card's second link, which only makes the caption
      // clickable). It used to count, because a[href] matched it and it has a
      // box, so it became the "last" control the wrap test compared with and
      // Tab from the real last control escaped the modal (Codex, 2026-10-06).
      if (el.tabIndex < 0) return false;
      // Hidden, inert or aria-hidden anywhere between the control and the
      // panel, and a computed visibility that leaves it unfocusable (the card
      // swatch links wait at visibility:hidden until hover).
      for (var n = el; n && n !== root; n = n.parentElement) {
        if (n.hasAttribute('hidden') || n.hasAttribute('inert') || n.getAttribute('aria-hidden') === 'true') return false;
      }
      var vis = getComputedStyle(el).visibility;
      if (vis === 'hidden' || vis === 'collapse') return false;
      return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    });
  }

  function onKeydown(e) {
    if (e.key !== 'Tab') return;
    var top = stack[stack.length - 1];
    if (!top) return;
    var items = focusable(top.panel);
    if (!items.length) { e.preventDefault(); return; }
    var first = items[0];
    var last = items[items.length - 1];
    // From the panel itself, where focus lands on open, Tab goes to the first
    // control and Shift+Tab to the last. The browser's own answer to
    // Shift+Tab there is the element before the panel, outside the trap.
    if (document.activeElement === top.panel) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (!top.panel.contains(document.activeElement)) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
    } else if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return {
    /* Whether any overlay the theme owns is on screen. The newsletter popup
       asks before it opens: showModal() draws in the top layer, above every
       panel here, so an unguarded timer lands on top of the cart drawer. */
    isOpen: function () { return stack.length > 0; },

    open: function (panel, initial) {
      if (!panel) return;
      var entry = { panel: panel, prev: document.activeElement };
      stack.push(entry);
      if (stack.length === 1) document.addEventListener('keydown', onKeydown, true);
      if (locks === 0) document.documentElement.style.overflow = 'hidden';
      locks++;

      // Focus lands on the panel itself unless the caller names a field.
      //
      // It used to land on the first focusable thing, which is the close
      // button in every overlay but two, and a button focused by script
      // matches :focus-visible whenever the last thing the shopper did was
      // not a click: on a timer, or from the keyboard. So the newsletter
      // popup opened with a black ring around its X, and the client read it
      // as a selected button on 2026-09-03. A field is different: it shows
      // its focus for mouse users too, and the search and back-in-stock
      // modals exist to be typed into, so those callers still pass one.
      //
      // The panel carries tabindex="-1" so it can take focus and stays out of
      // the tab order, and its own :focus outline is off in theme.css. Tab
      // from there goes to the first control inside, which is the X, and it
      // shows its ring then, when a keyboard user is looking for it.
      var target = initial || panel;
      if (target === panel && !panel.hasAttribute('tabindex')) {
        panel.setAttribute('tabindex', '-1');
      }
      // Two frames: panels animate in from display/visibility states that can't
      // take focus, so focusing synchronously silently does nothing.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          // preventScroll matters: a plain focus() makes the browser scroll the
          // focused element into view, and on a panel that is still translated
          // off-screen mid-transition that scrolls the dialog's own container
          // and visibly breaks the slide.
          // Only while it is still open: an overlay closed before these
          // frames ran used to take focus back after its close had returned it.
          if (stack.indexOf(entry) === -1) return;
          try { target.focus({ preventScroll: true }); } catch (err) { try { target.focus(); } catch (e) {} }
        });
      });
    },

    /* With a panel, its own entry goes, wherever it sits: a section torn
       down under another overlay must not close that one instead (Codex,
       2026-10-03). A panel with no entry closes nothing. With none, the top
       entry goes, as every caller assumed before. Focus goes back only when
       the entry left was the top one; under it, the top overlay keeps it. */
    close: function (panel) {
      if (!stack.length) return;
      var at = stack.length - 1;
      if (panel) {
        at = -1;
        for (var i = stack.length - 1; i >= 0; i--) { if (stack[i].panel === panel) { at = i; break; } }
        if (at === -1) return;
      }
      var wasTop = at === stack.length - 1;
      var top = stack.splice(at, 1)[0];
      // An overlay above the one taken out may have been opened from inside
      // it. Its way back now goes where the one taken out came from: closing
      // it later found its opener detached and dropped focus on the page
      // (Codex, follow-up review 3 of 2026-10-03).
      if (!wasTop && top && top.panel) {
        for (var j = at; j < stack.length; j++) {
          var back = stack[j].prev;
          if (back && (back === top.panel || top.panel.contains(back))) stack[j].prev = top.prev;
        }
      }
      if (!stack.length) document.removeEventListener('keydown', onKeydown, true);
      locks = Math.max(0, locks - 1);
      if (locks === 0) document.documentElement.style.overflow = '';
      if (wasTop && top) {
        // Back to the opener if it is still on the page; failing that, to the
        // overlay now on top. A detached node would leave focus on the body.
        var target = top.prev && top.prev.isConnected ? top.prev
          : (stack.length ? stack[stack.length - 1].panel : null);
        if (target && target.focus) {
          try { target.focus({ preventScroll: true }); } catch (err) { try { target.focus(); } catch (e) {} }
        }
      }
      // Announced so a deferred opener can try again. `stack` may still hold a
      // panel underneath, which is why listeners re-check isOpen() rather than
      // treating this as "the screen is clear".
      try {
        document.dispatchEvent(new CustomEvent('bt:modal:close'));
      } catch (err) {}
    }
  };
})();

/*
 * QUICK ADD
 * Size buttons on a product card put that exact variant in the bag without
 * leaving the grid. Delegated from the document so cards appended by infinite
 * scroll or "load more" work without rebinding.
 *
 * Deliberately shares the cart drawer's own refresh/open functions rather than
 * reimplementing them, so the free-shipping bar, counters and line items all
 * update through one path.
 */
function handleQuickAdd() {
  var STATUS_ID = 'bt-quickadd-status';

  // One shared live region: a per-card one would be announced on render, and
  // twenty-four of them is twenty-four things for a screen reader to track.
  function status(message) {
    var el = document.getElementById(STATUS_ID);
    if (!el) {
      el = document.createElement('p');
      el.id = STATUS_ID;
      el.className = 'visually-hidden';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.textContent = message;
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-quick-add]');
    if (!btn) return;

    // The card is covered by a stretched link; without this the click
    // navigates to the product instead of adding it.
    e.preventDefault();
    e.stopPropagation();

    if (btn.disabled) return;
    var id = btn.getAttribute('data-quick-add');
    if (!id) return;

    btn.disabled = true;
    btn.classList.add('is-loading');

    // Through BTCart, which re-reads the cart and tells every surface. Shopify
    // answers 422 with a description when the line can't be added (sold out
    // between page load and click, most often), and that rejects here. A
    // successful add whose cart read then fails still resolves: the line IS in
    // the bag, and the drawer says it could not be shown.
    // The size's terms, as the product form's add sends them, so the order
    // records them whichever way the line came in, and a size added here and
    // on the product page is one bag line: the same _final_sale, and the same
    // _preorder ship line (snippets/purchase-terms.liquid writes both).
    var line = { id: Number(id), quantity: 1 };
    var props = {};
    if (btn.hasAttribute('data-final-sale')) props._final_sale = '1';
    if (btn.hasAttribute('data-preorder')) props._preorder = btn.getAttribute('data-preorder');
    if (Object.keys(props).length) line.properties = props;
    BTCart.add({ items: [line] })
      .then(function () {
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        status(BT_T('added_to_bag', 'Added to your bag.'));
        setTimeout(function () { btn.classList.remove('is-added'); btn.disabled = false; }, 1200);
        if (window.openCartDrawer) window.openCartDrawer();
      })
      .catch(function (err) {
        btn.classList.remove('is-loading');
        btn.disabled = false;
        // Mark it unavailable rather than silently failing: if it sold out,
        // the card is now wrong and should say so. Not for an UNKNOWN outcome,
        // which says nothing about stock: BTCart has put the real bag in the
        // drawer, so open it and let the message say what happened.
        if (err && err.unknown) {
          status(err.message);
          if (window.openCartDrawer) window.openCartDrawer();
          return;
        }
        btn.classList.add('is-failed');
        var message = (err && err.message) ? err.message : BT_T('add_error', 'This item could not be added.');
        btn.title = message;
        status(message);
      });
  });
}

/*
 * FILTER SCROLL POSITION
 * Filters and sort are real links and a form, so choosing one is a navigation
 * and the browser lands at the top of the new page — a long way from whatever
 * the shopper was looking at. Remember where they were and put them back.
 *
 * Restoring on DOMContentLoaded, before images have loaded, only works because
 * every card carries width/height and the media boxes are aspect-locked, so the
 * grid's height is already correct at that point.
 */
function handleFilterScroll() {
  var KEY = 'bt_filter_scroll';

  function remember() {
    try {
      sessionStorage.setItem(KEY, JSON.stringify({
        path: window.location.pathname,
        y: window.scrollY
      }));
    } catch (err) {}
  }
  window.BTRememberScroll = remember;

  // Filter values, the active-filter pills, clear-all, and the sort form.
  document.addEventListener('click', function (e) {
    if (e.target.closest('.fopt, .factive__pill, .factive__clear, .fsheet__clear')) remember();
  });

  var grid = document.querySelector('[data-product-grid]');
  if (!grid) return;

  var saved = null;
  try {
    saved = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    sessionStorage.removeItem(KEY);
  } catch (err) {}
  if (!saved || saved.path !== window.location.pathname || !saved.y) return;

  var max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  if (saved.y <= max) {
    window.scrollTo(0, saved.y);
    return;
  }
  // Fewer results than before, so the old position is past the end of the page.
  // Land on the filter bar instead of at the bottom of a short page.
  var bar = document.querySelector('.filters, .fbar-mobile');
  if (bar) window.scrollTo(0, Math.max(0, bar.getBoundingClientRect().top + window.scrollY - 12));
}

/*
 * SWATCH PREVIEW
 * Hovering a colourway swatch on a card shows that colourway in the card's
 * image, for as long as it's hovered. Saves opening three products to see
 * three prints.
 *
 * Works on a collection card and on the product page gallery, from one
 * routine. The preview gets its own layer rather than swapping the image's src.
 * Swapping src is instant and can't be transitioned, so leaving the swatch
 * snapped back — entering only felt smooth because the hover image happened to
 * be fading out over the top of it and hid the swap. With a layer, both
 * directions are a real cross-fade, and out is slower than in so the card
 * settles rather than blinks.
 *
 * The layer is built on first use, so cards nobody hovers cost nothing, and
 * its src is left in place afterwards — coming back to the same swatch is then
 * instant instead of re-fetching.
 */
function handleSwatchPreview() {
  // Hover only. On touch there is no hover to preview during — a tap is a
  // navigation — so the whole thing stays out of the way there.
  if (!window.matchMedia || !window.matchMedia('(hover: hover)').matches) return;

  // Two surfaces: a swatch on a card previews into that card's image, one on a
  // product page into the main gallery image.
  function target(swatch) {
    var card = swatch.closest('.product-card');
    if (card) {
      var media = card.querySelector('.card__media');
      return media ? { host: card, mount: media } : null;
    }
    if (swatch.closest('.swatches--pdp')) {
      var main = document.querySelector('.gallery__main');
      return main ? { host: main, mount: main } : null;
    }
    return null;
  }

  function layer(mount) {
    var el = mount.querySelector('.bt-swatch-preview');
    if (el) return el;
    el = document.createElement('img');
    el.className = 'bt-swatch-preview';
    el.alt = '';
    el.setAttribute('aria-hidden', 'true');
    el.setAttribute('decoding', 'async');
    mount.appendChild(el);
    return el;
  }

  // The spinner is built on first need, like the preview layer, so a card
  // nobody hovers still costs nothing.
  function spinner(mount) {
    var el = mount.querySelector('.bt-swatch-spin');
    if (el) return el;
    el = document.createElement('span');
    el.className = 'bt-swatch-spin';
    el.setAttribute('aria-hidden', 'true');
    mount.appendChild(el);
    return el;
  }

  // Held back deliberately. A colourway already in cache is ready within a
  // frame or two, and flashing a spinner on every hover is worse than showing
  // none at all — only a preview that is genuinely slow announces itself.
  var SPIN_AFTER = 140;

  function stopSpin(host) {
    if (host.btSpinTimer) { clearTimeout(host.btSpinTimer); host.btSpinTimer = null; }
    host.classList.remove('is-swatch-loading');
  }

  function reveal(host) {
    stopSpin(host);
    host.classList.add('is-swatch-preview');
  }

  function show(swatch) {
    var url = swatch.getAttribute('data-swatch-image');
    var t = url && target(swatch);
    if (!t) return;
    var host = t.host;
    var el = layer(t.mount);

    // Which colourway this hover is asking for. A load that finishes after the
    // pointer has moved on must not reveal the wrong one, and sliding along a
    // swatch row does exactly that — so every completion checks this first.
    host.btSwatchWant = url;

    // Already decoded: straight in, no spinner, no wait. `complete` alone is
    // true for a failed load too, hence naturalWidth.
    if (el.getAttribute('src') === url && el.complete && el.naturalWidth) {
      reveal(host);
      return;
    }

    // Not ready yet. What to do with the layer depends on whether it has ever
    // loaded anything, and the test has to happen before src is reassigned
    // below, which resets `complete` straight away.
    //
    // Already showing a previous colourway: leave it up. An <img> keeps
    // painting its old frame until the new src decodes, so sliding along a
    // swatch row still goes A -> B with nothing blank in between — the
    // behaviour the fade timings in theme.css were tuned around. Only a layer
    // that has never loaded anything stays hidden, because that one really
    // would fade in empty; the card's own photo shows through instead.
    if (el.complete && el.naturalWidth) host.classList.add('is-swatch-preview');
    else host.classList.remove('is-swatch-preview');
    spinner(t.mount);
    stopSpin(host);
    host.btSpinTimer = setTimeout(function () {
      if (host.btSwatchWant === url) host.classList.add('is-swatch-loading');
    }, SPIN_AFTER);

    // Assigned rather than added so a previous colourway's pending handler is
    // discarded when the pointer moves to the next swatch.
    el.onload = function () {
      if (host.btSwatchWant === url) reveal(host);
    };
    // A swatch image that 404s should leave the card exactly as it was, not
    // spin forever.
    el.onerror = function () {
      if (host.btSwatchWant === url) stopSpin(host);
    };
    if (el.getAttribute('src') !== url) el.src = url;
  }

  function hide(swatch) {
    var t = target(swatch);
    if (!t) return;
    // Clearing this is what stops an in-flight load revealing itself after the
    // pointer has already left.
    t.host.btSwatchWant = null;
    stopSpin(t.host);
    t.host.classList.remove('is-swatch-preview');
  }

  // mouseover/mouseout rather than mouseenter/mouseleave: only the former pair
  // bubbles, and these are delegated so cards added by pagination work too.
  document.addEventListener('mouseover', function (e) {
    var sw = e.target.closest('[data-swatch-image]');
    if (sw) show(sw);
  });
  document.addEventListener('mouseout', function (e) {
    var sw = e.target.closest('[data-swatch-image]');
    // Ignore moves between the swatch's own children.
    if (sw && !sw.contains(e.relatedTarget)) hide(sw);
  });
  // Keyboard gets the same preview when tabbing the swatch row.
  document.addEventListener('focusin', function (e) {
    var sw = e.target.closest('[data-swatch-image]');
    if (sw) show(sw);
  });
  document.addEventListener('focusout', function (e) {
    var sw = e.target.closest('[data-swatch-image]');
    if (sw) hide(sw);
  });
}

/*
 * COLLECTION FILTER DROPDOWNS
 *
 * Was an inline script in main-collection-products.liquid, bound to the
 * <details> elements present at load. Filtering now replaces that markup in
 * place, so the binding has to be repeatable — hence a named function that
 * handleCollectionFilters() calls again after each swap.
 *
 * The per-element `toggle` listener cannot be delegated: toggle does not
 * bubble. The document-level click and keydown handlers are bound once, from
 * bindOnce, because those *do* survive a swap and would otherwise stack up.
 */
var btDropdownsBound = false;
function handleFilterDropdowns() {
  var root = document.querySelector('[data-collection-section]');
  if (!root) return;

  // The section on screen, read each time: the document listeners below are
  // bound once and outlive a theme editor reload of the section.
  function closeAll(except) {
    var live = document.querySelector('[data-collection-section]');
    if (!live) return;
    live.querySelectorAll('details.fdrop[open]').forEach(function (d) {
      if (d !== except) d.removeAttribute('open');
    });
  }

  root.querySelectorAll('details.fdrop').forEach(function (d) {
    if (d.__btDropBound) return;
    d.__btDropBound = true;
    d.addEventListener('toggle', function () {
      if (d.open) closeAll(d);
    });
  });

  if (btDropdownsBound) return;
  btDropdownsBound = true;
  document.addEventListener('click', function (e) {
    var scope = document.querySelector('[data-collection-section]');
    if (!scope || !scope.querySelector('details.fdrop[open]')) return;
    if (!e.target.closest('details.fdrop')) closeAll(null);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAll(null);
  });
}

/*
 * COLLECTION FILTERING, IN PLACE
 *
 * Filter options are ordinary links, and following one used to reload the page.
 * The theme compensated by saving scrollY to sessionStorage and restoring it on
 * DOMContentLoaded — which is why choosing a filter visibly threw the page to
 * the top and then yanked it back. The restore was the jump.
 *
 * Fetching the section and swapping it in means the scroll position is never
 * touched at all, so there is nothing to restore. The links keep working
 * untouched without JS, and any failure falls through to a normal navigation.
 *
 * Only handleCollectionPagination and handleFilterSheet hold references into
 * this markup; everything else here is delegated from the document and needs
 * no re-init. Those two are re-run after each swap.
 */
function handleCollectionFilters() {
  if (!document.querySelector('[data-collection-section]') || !window.history || !window.history.pushState) return;
  // Safe to run again. The listeners below go on once for the page, and each
  // swap works on the section on screen: a theme editor reload replaces the
  // section element itself, and the first version kept the old one, so links
  // in the new one fell back to full page loads (Codex follow-up review).
  if (handleCollectionFilters.bound) return;
  handleCollectionFilters.bound = true;

  var SELECTOR = '.fopt, .factive__pill, .factive__clear, .fsheet__clear';
  var loading = false;
  var section = null;
  /* The request in flight, and a counter naming the latest. A click while
     one is out is still ignored, because its link was built from the grid
     being replaced. Back and Forward are not: they supersede it, because the
     address bar has already moved. Dropping them left the old grid under the
     new address, and the pending response then pushed its own URL on top
     (Codex full-theme review, 2026-11-02, finding 4). */
  var pending = null;
  var seq = 0;

  function swap(url, push, supersede) {
    if (loading && !supersede) return;
    if (pending) { try { pending.abort(); } catch (e) {} pending = null; }
    section = document.querySelector('[data-collection-section]');
    if (!section) { window.location.href = url; return; }
    loading = true;
    section.setAttribute('aria-busy', 'true');
    var mine = ++seq;
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    pending = ctrl;

    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' }, signal: ctrl ? ctrl.signal : undefined })
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      })
      .then(function (text) {
        if (mine !== seq) return;
        pending = null;
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var next = doc.querySelector('[data-collection-section]');
        if (!next) throw new Error('section not found');

        /* Which panel was open, and what had focus.
         *
         * The swap replaces the filter bar along with the grid, because the
         * value counts change with every selection and a stale "(12)" is worse
         * than a reopened menu. That cost the open <details>: choosing xs shut
         * the panel, so choosing xs+s+m meant opening Size three times. The
         * open state and the focused option are read off the OLD markup here
         * and put back below.
         *
         * Matched on data-filter and the option's label rather than on node
         * identity, because every node in here is about to be replaced. */
        var reopen = [];
        [].forEach.call(section.querySelectorAll('.fdrop[open]'), function (d) {
          reopen.push(d.getAttribute('data-filter'));
        });
        var focusIn = null;
        var act = document.activeElement;
        if (act && section.contains(act)) {
          var grp = act.closest('.fdrop');
          focusIn = {
            filter: grp ? grp.getAttribute('data-filter') : null,
            label: (act.querySelector('.fopt__label') || act).textContent.trim(),
            summary: act.tagName === 'SUMMARY'
          };
        }

        // The filter sheet lives inside this markup. Replacing it while it is
        // open skips its close(), so the scroll lock and the Tab trap it took
        // out stayed on: Clear all inside the sheet left a phone unable to
        // scroll (2026-09-03). Its own close control releases both.
        var sheetClose = section.querySelector('.fsheet:not([hidden]) [data-sheet-close]');
        if (sheetClose) sheetClose.click();

        section.innerHTML = next.innerHTML;
        if (push) window.history.pushState({ btFilter: true }, '', url);

        // Re-init the two things that hold references into this markup. The
        // rest — swatch preview, quick add, wishlist — are delegated.
        handleFilterDropdowns();
        handleCollectionPagination();
        handleFilterSheet();

        /* Put the panel back before anything can paint it shut. `open` is set
         * directly rather than through a click, so no toggle handler runs and
         * the exclusive `name="filtergroup"` grouping is left to the browser. */
        reopen.forEach(function (name) {
          var d = name && section.querySelector('.fdrop[data-filter="' + name + '"]');
          if (d) d.open = true;
        });

        /* And the keyboard's place in it. The clicked option is still there
         * under the same label -- what changed is its href, from add to remove
         * -- so a keyboard user carries on down the list instead of being
         * dropped at the top of the document. */
        if (focusIn) {
          var scope = focusIn.filter
            ? section.querySelector('.fdrop[data-filter="' + focusIn.filter + '"]')
            : section;
          if (scope) {
            var target = null;
            if (focusIn.summary) {
              target = scope.querySelector('summary');
            } else {
              [].forEach.call(scope.querySelectorAll('.fopt'), function (a) {
                var l = a.querySelector('.fopt__label');
                if (!target && l && l.textContent.trim() === focusIn.label) target = a;
              });
            }
            if (target) try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
          }
        }

        section.removeAttribute('aria-busy');
        loading = false;
      })
      .catch(function () {
        // A superseded request ends here too, aborted or simply late; the
        // newer one owns the page.
        if (mine !== seq) return;
        pending = null;
        // Never strand the shopper on an unchanged page: fall back to the
        // navigation the link would have done anyway.
        loading = false;
        window.location.href = url;
      });
  }

  document.addEventListener('click', function (e) {
    var link = e.target.closest(SELECTOR);
    if (!link || !link.href) return;
    var live = document.querySelector('[data-collection-section]');
    if (!(live && live.contains(link)) && !link.closest('.fsheet')) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    swap(link.href, true);
  });


  window.addEventListener('popstate', function () {
    if (document.querySelector('[data-collection-section]')) swap(window.location.href, false, true);
  });
}

/*
 * THEME EDITOR: A SECTION RE-RENDERED
 * The editor replaces a section's HTML when one of its settings changes and
 * fires shopify:section:load on the new section. Delegated handlers need
 * nothing; these bind to elements, so they run again for the new ones. Each
 * is safe to run again: elements are bound once, and document and window
 * listeners go on once for the page. Running every ready() handler instead
 * would double the delegated ones. The nav's dropdown has its own listener
 * (handleCollectionsDropdown). Codex follow-up review, 2026-10-03.
 */
document.addEventListener('shopify:section:load', function (e) {
  var root = e.target && e.target.querySelector ? e.target : document;
  function has(sel) { return (root.matches && root.matches(sel)) || !!root.querySelector(sel); }
  if (has('.js--variant-option')) handleVariantSelector();
  if (has('#searchModal, [data-search-open]')) handleSearchModal();
  if (has('shopify-account')) btAccountStyle();
  if (has('[data-collection-section]')) {
    handleFilterDropdowns();
    handleCollectionFilters();
    handleCollectionPagination();
    handleFilterSheet();
  }
});
/* And before it takes one away: an overlay open inside the old section would
   leave its scroll lock and focus trap behind with nothing on screen to
   release them. Each closes through its own control. */
document.addEventListener('shopify:section:unload', function (e) {
  var root = e.target;
  if (!root || !root.querySelector) return;
  if (root.querySelector('#searchModal.is-open')) btSearchClose();
  // The mobile menu is a BTModal entry since 2026-11-02 (Codex, REVIEW-2 of
  // the full-theme fixes), so it closes here too.
  if (root.querySelector('#header-nav.is-open')) setNav(false);
  var sheetClose = root.querySelector('[data-sheet]:not([hidden]) [data-sheet-close]');
  if (sheetClose) sheetClose.click();
});

ready(function () {
  handleSwatchPreview();
  handleFilterScroll();
  handleFilterDropdowns();
  handleCollectionFilters();
  handleQuickAdd();
  handleNumberStepper();
  handleVariantSelector();
  handleSearchModal();
  handleCollectionsDropdown();
  handleCollectionPagination();
  handleFilterSheet();
});

/*
 * REMOVED: enforceInStockFilter()
 *
 * It watched for a `filter.v.option` parameter and, finding one without
 * `filter.v.availability`, did a client-side window.location.replace() — a full
 * second page load after the first had already rendered.
 *
 * It was mitigating Shopify's native option filters, which match on an option
 * *existing* rather than that variant being in stock. That problem is now
 * solved properly by the custom.in_stock_sizes metafield.
 *
 * Confirmed against the rendered storefront on 2026-08-03: Search & Discovery
 * exposes only `filter.p.m.custom.in_stock_sizes` and
 * `filter.p.m.custom.color_family`. No native option filter is enabled, so
 * `filter.v.option` is never generated — and `filter.v.availability` isn't an
 * enabled filter either, so the redirect couldn't have changed the results even
 * when it fired. Pure cost, no effect.
 *
 * If a native option filter is ever re-enabled in Search & Discovery, prefer
 * the in_stock_sizes metafield filter over reinstating this.
 */

/*
 * FORMAT MONEY
 * Formats the passed value as money with currency symbol.
 * Referenced from https://gist.github.com/stewartknapman/8d8733ea58d2314c373e94114472d44c
 */
var Shopify = Shopify || {};
// Respect the store's configured money format (injected from Liquid in
// theme.liquid). Only fall back to a default if it wasn't set, so variant
// price updates match the server-rendered format (e.g. no decimals).
Shopify.money_format = Shopify.money_format || '${{amount}}';
Shopify.formatMoney = function (cents, format) {
  if (typeof cents == 'string') {
    cents = cents.replace('.', '');
  }
  var value = '';
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = format || this.money_format;

  function defaultOption(opt, def) {
    return typeof opt == 'undefined' ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal = defaultOption(decimal, '.');

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split('.'),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
      cents = parts[1] ? decimal + parts[1] : '';

    return dollars + cents;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
      break;
  }

  return formatString.replace(placeholderRegex, value);
};

/*
 * OPTION SELECTOR
 * Works the variant option dropdowns used on the
 * product page template.
 */
function handleVariantSelector() {
  var variant;
  var options = [];

  // Seed from the variant the page rendered with. The template can skip an option
  // group (e.g. a Color with a single value), so its radio never exists in the DOM.
  // Without seeding, that option stays null and NO variant ever matches, leaving the
  // form pointing at the first available variant.
  options[1] = typeof current_variant !== 'undefined' && current_variant ? current_variant.option1 : null;
  options[2] = typeof current_variant !== 'undefined' && current_variant ? current_variant.option2 : null;
  options[3] = typeof current_variant !== 'undefined' && current_variant ? current_variant.option3 : null;

  // Safe to run again (the theme editor's section reload, below): each input
  // is bound once, marked on the node.
  var variantOptions = document.querySelectorAll('.js--variant-option');
  variantOptions.forEach(function (el) {
    if (el.__btVariantBound) return;
    el.__btVariantBound = true;
    el.addEventListener('change', function (event) {
      // Disable non-existent variant options.
      checkVariants(event);

      // Overlay the chosen options on top of the seeded defaults.
      variantOptions.forEach(function (opt) {
        if (opt.tagName.toLowerCase() == 'input' && opt.checked == true) {
          var optionName = opt.getAttribute('name');
          var optionPos = parseInt(optionName.replace('option', ''));
          var optionValue = opt.value;
          options[optionPos] = optionValue;
        }
      });

      // Find the variant matching the current selection.
      var match = null;
      variants.forEach(function (v) {
        if (v.option1 == options[1] && v.option2 == options[2] && v.option3 == options[3]) {
          match = v;
        }
      });

      /* checkVariants judges each other group against the value just chosen,
         one group at a time, so with three varying options it can leave a set
         that no single variant has: S/Regular/Short, M/Regular/Long and
         M/Tall/Short, then choosing M keeps Regular and Short. Nothing matched,
         this returned, and the form still posted S (Codex full-theme review,
         2026-11-02, finding 1). Now the selection moves to the real variant
         that keeps the value just chosen and most of the others, an available
         one first among equals, and the radios follow it. */
      if (!match && event.target && event.target.name) {
        var chosenPos = parseInt(event.target.name.replace('option', ''), 10);
        var best = null;
        var bestScore = -1;
        variants.forEach(function (v) {
          if (v['option' + chosenPos] != event.target.value) return;
          var score = v.available ? 1 : 0;
          for (var i = 1; i <= 3; i++) {
            if (i !== chosenPos && v['option' + i] == options[i]) score += 2;
          }
          if (score > bestScore) { best = v; bestScore = score; }
        });
        if (best) {
          match = best;
          for (var p = 1; p <= 3; p++) options[p] = best['option' + p];
          variantOptions.forEach(function (opt) {
            if (opt.tagName.toLowerCase() !== 'input') return;
            var pos = parseInt(opt.getAttribute('name').replace('option', ''), 10);
            if (pos !== chosenPos && opt.value == options[pos]) {
              opt.disabled = false;
              opt.checked = true;
            }
          });
        }
      }

      if (!match) {
        // No variant at all: the form must not keep posting the last one.
        console.error('No variant matched the selected options', options.slice(1));
        btShowNoVariant();
        return;
      }
      variant = match;

      // Update the variant id FIRST so the cart always posts the chosen variant,
      // even if a later UI update fails.
      var idInput = document.querySelector('input#js--variant-id');
      if (idInput) idInput.value = match.id;
      // The URL next, for the same reason: a reload or a shared link keeps the
      // choice whatever happens to the rest of the page.
      try { setVariantUrl(match); } catch (err) { console.error('Variant URL update failed:', err); }

      try {
        refreshSoldOutStates(options);
        updateVariantUI(match);
      } catch (err) {
        // Never let a cosmetic failure leave the form in a stale state.
        console.error('Variant UI update failed:', err);
      }
      try { showVariantImage(match); } catch (err) { console.error('Variant image update failed:', err); }
    });
  });

  // Run once for the selection the page rendered with, so a multi-option
  // product agrees with the server on first paint.
  if (variantOptions.length && typeof variants !== 'undefined') {
    try { refreshSoldOutStates(options); } catch (err) {}
  }
}

/* The selection matches no variant. Clears the id the form posts, so the
   submit handler in cart-drawer.js asks for a size instead of adding the
   previous variant, and shows the buy button as unavailable. */
function btShowNoVariant() {
  var idInput = document.querySelector('input#js--variant-id');
  if (idInput) idInput.value = '';
  var atcBtn = document.querySelector('#js--addtocart');
  if (atcBtn) {
    atcBtn.hidden = false;
    atcBtn.disabled = true;
    atcBtn.innerText = BT_T('no_variant', 'Unavailable');
  }
  var notifyBtn = document.querySelector('[data-notify-open]');
  if (notifyBtn) notifyBtn.hidden = true;
  var buyNow = document.querySelector('.js--buynow');
  if (buyNow) buyNow.hidden = true;
}

/* The buy button's state for whatever the form will post now, for a caller
   that changed it without a selection: cart-drawer.js, when an add finishes
   after the shopper picked another size mid-add (Codex full-theme review,
   2026-11-02, finding 3). Returns false off a product page, where there is no
   variant list to read, so the caller falls back to its own restore. */
window.btSyncBuyButton = function () {
  if (typeof variants === 'undefined' || !document.querySelector('#js--addtocart')) return false;
  var idInput = document.querySelector('input#js--variant-id');
  var id = idInput ? String(idInput.value) : '';
  var v = null;
  variants.forEach(function (x) { if (String(x.id) === id) v = x; });
  if (v) updateVariantUI(v); else btShowNoVariant();
  return true;
};

// Everything that reflects the selected variant in the UI. Kept separate so a
// failure here can't prevent the variant id from being set on the form.
function updateVariantUI(v) {
  // The following is used for the "purchase together" feature.
  document.querySelectorAll('input[type="checkbox"].js--variant-id').forEach(function (el) {
    el.value = v.id;
    el.setAttribute('data-price', v.price);
    if (v.available == true) {
      el.checked = true;
      el.disabled = false;
    } else {
      el.checked = false;
      el.disabled = true;
    }
  });

  document.querySelectorAll('.js--variant-price').forEach(function (el) {
    el.innerHTML = Shopify.formatMoney(v.price);
  });

  if (v.compare_at_price > v.price) {
    document.querySelectorAll('.js--variant-compareatprice').forEach(function (el) {
      el.innerText = Shopify.formatMoney(v.compare_at_price);
    });
  } else {
    document.querySelectorAll('.js--variant-compareatprice').forEach(function (el) {
      el.innerText = '';
    });
  }

  // Update SKU
  document.querySelectorAll('.js--variant-sku').forEach(function (el) {
    el.innerText = v.sku;
  });

  // Swap the buy button for the back-in-stock button when the chosen size
  // can't be bought. When the theme has no notify button to offer (the setting
  // is off, or this is a pre-order), fall back to the old disabled state.
  var atcBtn = document.querySelector('#js--addtocart');
  var notifyBtn = document.querySelector('[data-notify-open]');
  var soldOut = v.available === false;

  // Pre-order is a fact about the chosen size, not about the product: a
  // product can carry one continue-selling size among genuinely sold-out ones.
  var preorderNow = btVariantIsPreorder(v);

  if (atcBtn) {
    atcBtn.disabled = soldOut;
    atcBtn.hidden = soldOut && !!notifyBtn;
    atcBtn.innerText = soldOut
      ? BT_T('sold_out', 'Sold out')
      : (preorderNow
          ? (atcBtn.getAttribute('data-label-preorder') || 'Pre-order')
          : (atcBtn.getAttribute('data-label') || BT_T('add_to_bag', 'Add to bag')));
  }
  if (notifyBtn) notifyBtn.hidden = !soldOut;

  // Keep what the bag is told in step with what the button says. The property
  // is disabled rather than removed, because a disabled control isn't
  // submitted — and it has to stay in the DOM to be switched back on.
  var preorderProp = document.querySelector('.js--preorder-prop');
  if (preorderProp) preorderProp.disabled = !preorderNow;
  var preorderNote = document.querySelector('.js--preorder-note');
  if (preorderNote) preorderNote.hidden = !preorderNow;

  // Buy it now goes with Add to bag. Offering express checkout beside a
  // "Notify me" button would be offering a purchase that can't be made.
  var buyNow = document.querySelector('.js--buynow');
  if (buyNow) buyNow.hidden = soldOut;

  updateLowStock(v);
}

/*
 * Is this variant a pre-order? Reads the per-variant map the product section
 * emits; a tagged product reports true for every variant, so the two routes in
 * converge here. Falls back to the old product-level flag so a cached copy of
 * one file against a fresh copy of the other degrades to the previous
 * behaviour rather than throwing.
 */
function btVariantIsPreorder(v) {
  if (v && typeof bt_preorder_variants !== 'undefined' && bt_preorder_variants) {
    var hit = bt_preorder_variants[String(v.id)];
    if (typeof hit === 'boolean') return hit;
  }
  return (typeof bt_is_preorder !== 'undefined') ? bt_is_preorder : false;
}

/*
 * LOW STOCK
 * Mirrors the Liquid that renders the initial state: only when stock is
 * actually tracked, the count is positive but at or below the threshold, and
 * the product isn't a pre-order (those sit at 0 with continue-selling, so an
 * urgency line there would be both wrong and a little cynical).
 */
function updateLowStock(v) {
  var el = document.querySelector('.js--low-stock');
  if (!el || !v) return;
  var text = el.querySelector('.js--low-stock-text');

  var qty = (typeof bt_inventory !== 'undefined' && v) ? bt_inventory[String(v.id)] : null;
  var threshold = (typeof bt_low_stock_threshold !== 'undefined') ? bt_low_stock_threshold : 3;
  var preorder = btVariantIsPreorder(v);

  var show = !preorder && v && v.available === true &&
    qty !== null && qty !== undefined && qty > 0 && qty <= threshold;

  if (show) {
    // Name the size, so the line reads as being about the thing just selected
    // rather than the product as a whole. bt_size_pos is 0 when there's no
    // size option, or only one, in which case naming it says nothing.
    var size = '';
    if (typeof bt_size_pos !== 'undefined' && bt_size_pos) {
      size = v['option' + bt_size_pos] || '';
    }
    if (text) {
      text.textContent = size
        ? BT_T('low_stock_size', 'Only %d left in %s')
            .replace('%d', qty).replace('%s', String(size).toUpperCase())
        : BT_T('low_stock', 'Only %d left').replace('%d', qty);
    }
    el.hidden = false;
  } else {
    el.hidden = true;
    if (text) text.textContent = '';
  }
}

/*
 * THE CHOSEN VARIANT'S PHOTO AND URL
 * Every product page, whatever it shows about stock. Both lived at the end of
 * updateLowStock until 2026-10-15, under its "no low-stock line here" return,
 * so a pre-order, a product without tracked stock, or low-stock messages
 * switched off kept the first photo and the first URL (Codex audit, finding 2).
 * The size-change handler calls them on their own.
 */
// The gallery goes to the variant's image, if it has one.
function showVariantImage(v) {
  if (!v || v.featured_image == null) return;
  var thumb = document.querySelector(
    '.gallery__thumbs button[data-imageid="' + v.featured_image.id + '"]'
  );
  if (thumb) thumb.click();
}

// The variant id goes on the URL so a reload or a shared link keeps the choice.
// Set ON the current URL: writing '?variant=' + id from nothing discarded every
// other parameter, so picking a size wiped utm_*, fbclid, gclid and any
// affiliate tag off the highest-intent traffic on the site.
function setVariantUrl(v) {
  if (!v || !historyState()) return;
  try {
    var url = new URL(window.location.href);
    url.searchParams.set('variant', v.id);
    window.history.replaceState({}, '', url.toString());
  } catch (e) {
    window.history.replaceState({}, '', '?variant=' + v.id);
  }
}

/*
 * REFRESH SOLD-OUT STATES
 * Marks each option value sold out when no purchasable variant exists for it,
 * holding the other groups' current selections fixed. Sold out is deliberately
 * not `disabled`: a disabled radio is skipped entirely by keyboard and screen
 * reader, and selecting the size is how a shopper asks to be told about a
 * restock.
 *
 * Liquid computes this server-side for the initial paint, but only per option
 * value in isolation — on a product with two real option groups the answer
 * changes as the other group is selected, which is what this keeps current.
 */
function refreshSoldOutStates(options) {
  document.querySelectorAll('.js--variant-option').forEach(function (input) {
    var pos = parseInt(input.getAttribute('name').replace('option', ''), 10);
    if (!pos) return;

    var candidate = options.slice();
    candidate[pos] = input.value;

    var available = variants.some(function (v) {
      for (var i = 1; i <= 3; i++) {
        // Only constrain the positions this product actually uses.
        if (candidate[i] != null && v['option' + i] != candidate[i]) return false;
      }
      return v.available === true;
    });

    // Only write when the answer actually changed. This runs over every radio
    // on every selection, and a redundant class write restarts the label's
    // transition on sizes the shopper didn't touch.
    var wasSoldOut = input.getAttribute('data-soldout') === 'true';
    if (wasSoldOut === available) {
      if (available) input.removeAttribute('data-soldout');
      else input.setAttribute('data-soldout', 'true');

      var label = input.nextElementSibling;
      if (!label || label.tagName !== 'LABEL') {
        label = input.parentNode ? input.parentNode.querySelector('label') : null;
      }
      if (label) label.classList.toggle('is-soldout', !available);

      /* Keep the accessible name honest. The chip's visible text is just the
         size; the state lives in the hidden span beside it, which Liquid
         renders once at page load. Changing colour changes which sizes are
         available, so without this a screen reader kept reading the state the
         page happened to load with.
         No aria-disabled: this control stays operable, because selecting a
         sold-out size is how a shopper reaches the back-in-stock form. */
      if (label) {
        var sr = label.querySelector('.js--size-a11y');
        if (sr) {
          var value = sr.getAttribute('data-size-value') || sr.textContent.trim();
          sr.textContent = available
            ? value
            : value + BT_T('size_sold_out', ', sold out');
        }
      }
    }
  });
}

/*
 * CHECK VARIANT EXISTS
 * This checks if the variant actually exists - e.g you may have small, medium,
 * and large in blue and black, but blue/medium is not a variant. Disables the
 * related inputs.
 */
function checkVariants(evt) {
  // Prefer the passed event; fall back to the legacy implicit global.
  var e = evt || (typeof event !== 'undefined' ? event : null);
  let $this = e ? e.target : undefined;
  if ($this !== undefined && $this !== null) {
    let availableVariants = new Set();
    variants.filter(function (variant, k) {
      if (variant[$this.name] == $this.value) {
        availableVariants.add(variant);
      }
    });

    // Loop through the array above to create an array containing available
    // option values.
    let optionGroups = {};
    availableVariants.forEach(function (variant) {
      let options = Object.entries(variant);
      for (const [key, value] of options) {
        if (value != null) {
          if (optionGroups[key] == undefined) {
            optionGroups[key] = [];
          }
          if (optionGroups[key].includes(value) == false) {
            optionGroups[key].push(value);
          }
        }
      }
    });

    // Then, loop through each input and check if its value is in the optionGroups
    // array created above, ignoring the clicked option group.
    document.querySelectorAll('.js--variant-option').forEach(function (input) {
      if (input.name != $this.name) {
        // `disabled` now means one thing only: this combination doesn't exist.
        // Sold-out-but-real values stay enabled so they can be selected and a
        // back-in-stock alert requested against them; refreshSoldOutStates
        // below is what marks those.
        var group = optionGroups[input.name];
        var exists = !!group && group.includes(input.value);
        input.disabled = !exists;
        if (!exists) input.checked = false;
      }
    });
  }

  // Check a valid option is selected for each group.
  document.querySelectorAll('.js--variant-options').forEach(function (group) {
    let firstAvailable = null;
    let checkedOptions = group.querySelectorAll('.js--variant-option:checked').length;
    if (checkedOptions == 0) {
      firstAvailable = group.querySelectorAll('.js--variant-option:not(:disabled)')[0];
    }
    if (firstAvailable != null) {
      firstAvailable.checked = true;
    }
  });
}

/*
 * TOGGLE MOBILE NAV
 * Open/close main nav on mobile via the burger button.
 */
/* Whether each of the drawer's groups comes up open. Every group remembers its
   own state: close Customer care and it stays closed, open Info and it stays
   open, and neither touches Collections. One shared flag used to cover them
   all, so only the shop list could be left the way a shopper left it -- the
   secondary groups came back shut every time, and closing one of them wrote
   "closed" over the shop list too. Client-reported 2026-09-24.

   A group nobody has touched takes its default: the shop list open, because
   that is what the menu is for on a phone, and the secondary groups closed,
   because they are a table of contents.

   Kept for the rest of the visit and no longer, which is the difference
   between sessionStorage and localStorage here: a shopper returning next week
   should not still be living with one tap they made once.

   Wrapped because Safari in private mode throws on access rather than
   returning null; when we cannot read, every group takes its default. */
var BT_NAV_GROUPS = 'bt-nav-groups';
function btNavGroupState() {
  try { return JSON.parse(sessionStorage.getItem(BT_NAV_GROUPS) || '{}') || {}; }
  catch (e) { return {}; }
}
function btNavGroupOpen(item, state) {
  var key = item.getAttribute('data-nav-group');
  if (key && Object.prototype.hasOwnProperty.call(state, key)) return state[key] === true;
  return !item.classList.contains('nav__item--secondary');
}
function btNavGroupRemember(item, isOpen) {
  var key = item.getAttribute('data-nav-group');
  if (!key) return;
  var state = btNavGroupState();
  state[key] = isOpen;
  try { sessionStorage.setItem(BT_NAV_GROUPS, JSON.stringify(state)); }
  catch (e) {}
}

/* The drawer's accordion animates `max-height`, and a stylesheet has to name a
 * number it cannot know. Whatever it names is the number the animation
 * interpolates through, so any overshoot is dead time: 420px against 189.6px of
 * real content spent a third of the .3s shrinking space nobody could see, and
 * the close appeared to stall and then rush.
 *
 * Measuring at toggle time makes the panel move on the first frame. Drawer
 * only -- above 769px .nav__menu is an absolutely positioned popover with no
 * max-height at all, and an inline one there would clip a long menu and go
 * stale on the next resize, so that width clears it instead. */
function btSyncNavMenuHeight(item, instant) {
  const menu = item.querySelector('.nav__menu');
  if (!menu) return;
  /* A shut group in the drawer is clipped to max-height 0 but not hidden, so
     its links stayed in the tab order and took focus while out of sight
     (Codex typography pass, 2026-10-06, D4). `inert` takes them out of both
     the order and the accessibility tree, and is cleared on desktop, where
     the menu is a popover. */
  menu.inert = window.innerWidth < 769 && !item.classList.contains('is-open');
  if (window.innerWidth >= 769) { menu.style.maxHeight = ''; return; }
  // scrollHeight is the rows themselves; the 14px of air under an open group is
  // a margin outside this box and animates on its own.
  const to = item.classList.contains('is-open') ? menu.scrollHeight + 'px' : '0px';

  /* `instant` is for the moments the drawer itself is moving.
   *
   * The groups are set at the same tick the drawer starts sliding, so without
   * this the Collections list UNFOLDS while the panel arrives -- 300ms of the
   * menu growing inside a menu that is still coming in, which reads as the
   * thing expanding under your thumb. Client-reported 2026-09-11.
   *
   * It was there before and quieter: max-height ran to 420px, so it passed the
   * real 189.6 at 45% of the curve and looked finished early. Measuring the
   * true height made the unfold last the whole slide and put it in plain sight.
   * A group that is open when the drawer opens should simply BE open. */
  if (instant) {
    const held = menu.style.transition;
    menu.style.transition = 'none';
    menu.style.maxHeight = to;
    menu.getBoundingClientRect();   // commit it before the transition comes back
    menu.style.transition = held;
  } else {
    menu.style.maxHeight = to;
  }
}

// The flag is a property of the function, not a `var x = false` beside it:
// ready() runs its callback at once when theme.js loads after parsing, so the
// first call comes before the line that would set the var, and that line then
// reset the flag behind it (see handleCollectionsDropdown).
function btBindNavMenuResize() {
  if (btBindNavMenuResize.bound) return;
  btBindNavMenuResize.bound = true;
  // The first sync, so a group that starts shut is inert before anyone tabs.
  document.querySelectorAll('.nav__item').forEach(function (item) { btSyncNavMenuHeight(item, true); });
  window.addEventListener('resize', function () {
    document.querySelectorAll('.nav__item').forEach(btSyncNavMenuHeight);
  });
  // The measured heights are cached inline, so a face that arrives after an
  // open group was measured (or a text-only resize) could leave it clipped.
  // Re-measure once the faces settle (Codex typography pass, D3).
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      document.querySelectorAll('.nav__item').forEach(function (item) { btSyncNavMenuHeight(item, true); });
    });
  }
}

function setNav(open) {
  const nav = document.querySelector('#header-nav');
  if (!nav) return;
  const wasOpen = nav.classList.contains('is-open');
  nav.classList.toggle('is-open', open);

  const scrim = document.querySelector('.nav-scrim');
  if (scrim) scrim.classList.toggle('is-open', open);

  /* Locks the page behind the drawer. Mobile-only in CSS, so this class is
     harmless on desktop where .nav is a bar in the flow and never "opens". */
  document.body.classList.toggle('nav-open', open);

  /* Each group opens the way the shopper last left it this visit, or at its
     default -- see btNavGroupOpen.

     Tied to the drawer's own state rather than set in the markup, because
     .nav__item.is-open also drives the DESKTOP dropdown: an item marked open in
     Liquid would leave that panel permanently down. Closing the drawer closes
     every group with it, so resizing up to the bar cannot strand one open. */
  const state = btNavGroupState();
  document.querySelectorAll('.nav__item').forEach(function (item) {
    const want = open && btNavGroupOpen(item, state);
    item.classList.toggle('is-open', want);
    // The drawer is opening or closing around this, so the group takes its
    // state without animating into it.
    btSyncNavMenuHeight(item, true);
    const t = item.querySelector('.nav__trigger');
    if (t) t.setAttribute('aria-expanded', want ? 'true' : 'false');
  });

  const burger = document.querySelector('.burger');
  if (burger) {
    burger.classList.toggle('is-open', open);
    burger.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  /* The open drawer is a modal like the theme's other overlays, through the
     same BTModal: focus moves into it, Tab stays inside, focus goes back to
     the burger on close, and the newsletter popup waits for it to close.
     It set classes only, so focus stayed behind the scrim and the popup
     opened over the menu on a first visit (Codex full-theme review,
     2026-11-02, finding 10; reproduced on live). Only on a change of state,
     so a repeated close never pops another overlay's entry. */
  if (window.BTModal) {
    if (open && !wasOpen) window.BTModal.open(nav);
    else if (!open && wasOpen) window.BTModal.close(nav);
  }
}

/* Widening past the phone layout with the drawer open closes it. The bar has
   no drawer to be open, and the drawer's modal entry would otherwise keep Tab
   trapped in the bar and the page's scroll locked. The burger's own display
   says which layout is on screen, so this follows the CSS breakpoint. */
(function () {
  var timer = null;
  window.addEventListener('resize', function () {
    clearTimeout(timer);
    timer = setTimeout(function () {
      const nav = document.querySelector('#header-nav');
      const burger = document.querySelector('.burger');
      if (nav && burger && nav.classList.contains('is-open') && getComputedStyle(burger).display === 'none') setNav(false);
    }, 150);
  }, { passive: true });
})();

function toggleNav() {
  const nav = document.querySelector('#header-nav');
  if (!nav) return;
  setNav(!nav.classList.contains('is-open'));
}

/* The drawer covers the page, so it needs the ways out that a covering thing
   needs: the scrim, Escape, and the close button inside it. The burger alone
   was enough while the menu pushed the page down and the header came with it. */
document.addEventListener('click', function (e) {
  if (e.target.closest && e.target.closest('[data-nav-close]')) setNav(false);
});
document.addEventListener('keydown', function (e) {
  if (e.key !== 'Escape') return;
  const nav = document.querySelector('#header-nav');
  if (nav && nav.classList.contains('is-open')) setNav(false);
});

/*
 * NUMBER INPUT STEPPER
 * Number stepper used for the quantity field.
 */
function handleNumberStepper() {
  /* Delegated, and bound once. The cart page re-renders its lines from the
     server after every change (BTCart), and per-element listeners bound at
     load would be left on the old buttons. */
  if (handleNumberStepper.bound) return;
  handleNumberStepper.bound = true;
  document.addEventListener('click', function (event) {
    var button = event.target.closest('.input-stepper button');
    if (!button) return;
    var input = button.closest('.input-stepper').querySelector('input[type="number"]');
    if (!input) return;
    var maxValue = input.hasAttribute('max') ? parseInt(input.getAttribute('max'), 10) : 20;
    var minValue = input.hasAttribute('min') ? parseInt(input.getAttribute('min'), 10) : 1;
    var currentValue = parseInt(input.value, 10) || 0;
    var changed = false;
    if (button.classList.contains('input-stepper_decrease') && currentValue > minValue) {
      input.value = currentValue - 1;
      changed = true;
    }
    if (button.classList.contains('input-stepper_increase') && currentValue < maxValue) {
      input.value = currentValue + 1;
      changed = true;
    }
    // Fire the same event the input's oninput handler listens for, so typed
    // and clicked changes go through one debounced path.
    if (changed) input.dispatchEvent(new Event('input', { bubbles: true }));
  });
}

/*
 * ACCOUNT PANEL
 * Shopify's <shopify-account> fires `open` before it shows its sheet. Its top
 * is set from the icon's bottom edge, read then, so the sheet hangs from the
 * header whether the shipping strip shows or the page has scrolled (Horizon's
 * header.js does the same). Captured on the document, so the theme editor's
 * re-rendered header needs nothing re-bound.
 */
document.addEventListener('open', function (e) {
  var el = e.target;
  if (!(el instanceof HTMLElement) || !el.matches('shopify-account')) return;
  var bottom = Math.max(0, Math.round(el.getBoundingClientRect().bottom));
  el.style.setProperty('--account-dialog-top', bottom + 'px');
}, true);

/* The sheet's own styles (snippets/account-sheet-style.liquid) go into each
   component's shadow root, beside the wrapper Shopify re-renders, so a render
   leaves them in place. The snippet's script puts them in as Shopify creates
   the root; this is the fallback, for a root made before it ran. Called
   again from shopify:section:load for a re-rendered header. */
function btAccountStyle() {
  var tpl = document.getElementById('bt-account-style');
  if (!tpl || !window.customElements) return;
  customElements.whenDefined('shopify-account').then(function () {
    document.querySelectorAll('shopify-account').forEach(function (el) {
      var root = el.shadowRoot;
      if (!root || el.btAccount) return;
      el.btAccount = true;
      if (!root.querySelector('style[data-bt]')) {
        var style = tpl.content.querySelector('style').cloneNode(true);
        style.setAttribute('data-bt', '');
        root.insertBefore(style, root.firstChild);
      }
      // Shopify draws the signed-in disc after it asks for the customer.
      new MutationObserver(btAccountSnapSoon).observe(root, { childList: true, subtree: true });
    });
    btAccountSnap();
  });
}
btAccountStyle();

/* The avatar's nudge onto a device pixel (ON WHOLE PIXELS in the snippet):
   the header centres the icon wherever its text's line heights put it, a
   half or a third of a pixel off the grid, and the disc and its letter round
   apart there. The disc itself is measured once the nudge is cleared (the
   host, before a customer's disc exists). Read again when Shopify draws the
   disc, after the fonts load and on resize, which move it. Vertical only:
   the letter's width varies, and a disc snapped sideways put a W 0.5px off
   centre where the unsnapped one measured 0.1. */
function btAccountSnap() {
  document.querySelectorAll('shopify-account').forEach(function (el) {
    el.style.removeProperty('--acct-snap');
    var disc = el.shadowRoot && el.shadowRoot.querySelector('.account-button__avatar');
    var dpr = window.devicePixelRatio || 1;
    var y = (disc || el).getBoundingClientRect().top + window.scrollY;
    var dy = Math.round(y * dpr) / dpr - y;
    if (Math.abs(dy) > .01) el.style.setProperty('--acct-snap', dy.toFixed(3) + 'px');
  });
}
function btAccountSnapSoon() {
  if (btAccountSnap.queued) return;
  btAccountSnap.queued = true;
  requestAnimationFrame(function () { btAccountSnap.queued = false; btAccountSnap(); });
}
if (document.fonts) document.fonts.ready.then(btAccountSnap);
window.addEventListener('resize', btAccountSnapSoon);

/*
 * SEARCH MODAL
 * Full-width slide-down search panel triggered from the header.
 */
function handleSearchModal() {
  // Safe to run again: the theme editor replaces the header section, modal
  // and all. Buttons are bound once each, marked on the node; the window and
  // document listeners go on once for the page and read the modal on screen.
  // They used to close over the first modal, so after a reload the search
  // button opened nothing (Codex follow-up review, 2026-10-03).
  var sm = document.getElementById('searchModal');
  if (!sm) return;

  if (!handleSearchModal.bound) {
    handleSearchModal.bound = true;
    window.addEventListener('resize', function () {
      var live = document.getElementById('searchModal');
      if (live && live.classList.contains('is-open')) btSearchPlace();
    });
    document.addEventListener('keydown', function (e) {
      var live = document.getElementById('searchModal');
      if (e.key === 'Escape' && live && live.classList.contains('is-open')) btSearchClose();
    });
  }

  document.querySelectorAll('[data-search-open]').forEach(function (btn) {
    if (btn.__btSearchBound) return;
    btn.__btSearchBound = true;
    btn.addEventListener('click', btSearchOpen);
  });
  sm.querySelectorAll('[data-search-close]').forEach(function (el) {
    if (el.__btSearchBound) return;
    el.__btSearchBound = true;
    el.addEventListener('click', btSearchClose);
  });
}

/* The panel and its scrim start below the top bar so the logo, account,
   wishlist and bag stay visible and usable while searching. Measured rather
   than assumed: the bar's height changes with the shipping note, the
   viewport and whether the page has scrolled, so a hardcoded offset would be
   wrong on most of them. */
function btSearchPlace() {
  var bar = document.querySelector('.topbar');
  var y = bar ? Math.max(0, Math.round(bar.getBoundingClientRect().bottom)) : 0;
  document.documentElement.style.setProperty('--search-top', y + 'px');
}

function btSearchOpen(e) {
  if (e) e.preventDefault();
  var sm = document.getElementById('searchModal');
  if (!sm || sm.classList.contains('is-open')) return;
  btSearchPlace();
  sm.classList.add('is-open');
  sm.setAttribute('aria-hidden', 'false');
  document.body.classList.add('search-open');
  window.BTModal.open(sm.querySelector('.search-modal__panel') || sm, document.getElementById('searchInput'));
}

function btSearchClose() {
  var sm = document.getElementById('searchModal');
  if (!sm || !sm.classList.contains('is-open')) return;
  sm.classList.remove('is-open');
  sm.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('search-open');
  window.BTModal.close();
}

/*
 * COLLECTIONS DROPDOWN
 * One open/close for every input (Codex late review, 2026-10-02): the panel
 * shows from .is-open alone, and aria-expanded always says the same thing.
 *
 *   mouse     opens on pointer entry, closes on leave unless keyboard focus
 *             is inside; a click keeps it open
 *   keyboard  Enter or Space toggles it; Tab walks into the links; focus
 *             leaving the item closes it; Escape closes it and puts focus back
 *             on the trigger
 *   touch     a tap toggles it; a tap anywhere else closes it
 *   drawer    below 769 a tap toggles the group and remembers it
 *
 * It was CSS :hover above 769 with the click handler returning early on any
 * hover-capable device, so a keyboard could not open it at all and hover never
 * set aria-expanded. Opening on pointerenter rather than :hover is also what
 * keeps Escape closed: the pointer resting on the item fires nothing until it
 * leaves and comes back. html.bt-nav-js keeps the old CSS hover for a page
 * where this never ran.
 */
/* Safe to call again. Each .nav__item is bound once, marked on the node, and
   the document listeners (outside click, Escape, the editor's section load)
   go on once for the page. The theme editor replaces the nav section's HTML
   when a setting changes: the html.bt-nav-js flag survives, which turns the
   CSS hover fallback off, so the replacement items must be bound or the
   dropdown is dead (Codex, final review 2026-10-03). Calling the old version
   again would have doubled every listener on the items that survived.

   The once-only flag lives on the function. As `var btNavDropdownBound =
   false` it was reset after the first call (ready() runs that call before
   execution reaches the var's line), and the editor's first section load
   bound the document listeners a second time; tools/nav-keyboard.py caught
   it on the store, 2026-10-03. */
function handleCollectionsDropdown(root) {
  btBindNavMenuResize();
  document.documentElement.classList.add('bt-nav-js');
  var canHover = window.matchMedia('(hover: hover)');

  function setItem(navItem, open) {
    var trigger = navItem.querySelector('.nav__trigger');
    navItem.classList.toggle('is-open', open);
    btSyncNavMenuHeight(navItem);
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  }
  function closeAllNavItems(except) {
    document.querySelectorAll('.nav__item.is-open').forEach(function (openItem) {
      if (openItem !== except) setItem(openItem, false);
    });
  }

  (root || document).querySelectorAll('.nav__item').forEach(function (navItem) {
    var trigger = navItem.querySelector('.nav__trigger');
    if (!trigger || navItem.__btNavBound) return;
    navItem.__btNavBound = true;

    trigger.addEventListener('click', function (e) {
      e.preventDefault();
      // Below 769px this IS the drawer: the tap toggles the group and is a
      // preference to remember.
      if (window.innerWidth < 769) {
        var openNow = !navItem.classList.contains('is-open');
        setItem(navItem, openNow);
        btNavGroupRemember(navItem, openNow);
        return;
      }
      // A mouse click on a hovered trigger keeps the panel it already opened;
      // toggling would shut it under the pointer. A keyboard activation
      // (detail 0) and a tap on a device that cannot hover toggle.
      var mouseClick = e.detail > 0 && canHover.matches && e.pointerType !== 'touch';
      var open = mouseClick ? true : !navItem.classList.contains('is-open');
      if (open) closeAllNavItems(navItem);
      setItem(navItem, open);
    });

    navItem.addEventListener('pointerenter', function (e) {
      if (window.innerWidth < 769 || e.pointerType !== 'mouse') return;
      closeAllNavItems(navItem);
      setItem(navItem, true);
    });
    navItem.addEventListener('pointerleave', function (e) {
      if (window.innerWidth < 769 || e.pointerType !== 'mouse') return;
      // Keyboard focus inside holds the panel open; the focus a mouse click
      // leaves on the trigger does not, or it would stay down after the
      // pointer left.
      var a = document.activeElement;
      if (navItem.contains(a) && a.matches(':focus-visible')) return;
      setItem(navItem, false);
    });
    // Focus leaving the item, Tab past the last link or Shift+Tab off the
    // trigger, closes the panel it opened.
    navItem.addEventListener('focusout', function (e) {
      if (window.innerWidth < 769) return;
      if (e.relatedTarget && navItem.contains(e.relatedTarget)) return;
      if (navItem.matches(':hover') && canHover.matches) return;
      setItem(navItem, false);
    });
  });

  if (handleCollectionsDropdown.bound) return;
  handleCollectionsDropdown.bound = true;

  // The editor's re-render of the nav: bind the items it put in.
  document.addEventListener('shopify:section:load', function (e) {
    handleCollectionsDropdown(e.target && e.target.querySelectorAll ? e.target : document);
  });

  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('.nav__item')) return;
    /* Desktop only. In the drawer the submenu is PART of the panel rather than
       a popover over the page, so "tap anywhere else to close it" is wrong --
       and the burger that opens the drawer is itself outside .nav__item, so
       this fired on the very click that opened the menu and stripped the
       is-open setNav had just applied. Collections opened and shut in the same
       frame, which is why it looked like it was never opening at all. */
    if (window.innerWidth < 769) return;
    closeAllNavItems();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || window.innerWidth < 769) return;
    var openItem = document.querySelector('.nav__item.is-open');
    if (!openItem) return;
    var trigger = openItem.querySelector('.nav__trigger');
    var focusWasInside = openItem.contains(document.activeElement);
    setItem(openItem, false);
    if (focusWasInside && trigger) trigger.focus();
  });
}

/*
 * COLLECTION PAGINATION
 * Supports "infinite" (auto-load on scroll) and "load_more" (button) modes by
 * fetching the next page URL, extracting its product grid, and appending the
 * new cards. "numbered" mode uses plain links and needs no JS.
 */
function handleCollectionPagination() {
  // A filter swap replaces the grid and calls this again. The last call's
  // request and observer go first: a page 2 of the old grid that answered
  // after the swap used to write the old page's URL into history and recount
  // the new grid (Codex follow-up review, 2026-10-03).
  if (handleCollectionPagination.teardown) handleCollectionPagination.teardown();
  handleCollectionPagination.teardown = null;
  // The scroll fallback below loads through whichever grid is on screen; a
  // call that finds no paginated grid leaves it nothing to load.
  handleCollectionPagination.loadNext = null;
  var grid = document.querySelector('[data-product-grid]');
  if (!grid) return;

  var mode = grid.getAttribute('data-pagination-style');
  if (mode !== 'infinite' && mode !== 'load_more') return;

  var status = document.querySelector('[data-infinite-status]');
  var loadMoreBtn = document.querySelector('[data-load-more]');
  var loading = false;
  var live = true;
  var pending = null;
  var filling = null;
  var observer = null;
  handleCollectionPagination.teardown = function () {
    live = false;
    if (pending) pending.abort();
    if (filling) filling.abort();
    if (observer) observer.disconnect();
  };
  // Whether an answer still belongs on screen: this call's grid, in the page.
  function current() { return live && grid.isConnected; }

  function nextUrl() {
    return grid.getAttribute('data-next-url');
  }

  function setStatus(html) {
    if (status) status.innerHTML = html || '';
  }

  function updateShowingCount() {
    // The number sits inside a translated sentence ("Showing 24 of 50"), so
    // the first integer in it is the one to move. It used to look for an
    // element nothing carried, and read "Showing 24" under 48 cards.
    var el = document.querySelector('[data-showing-count]');
    if (!el) return;
    var n = grid.querySelectorAll('.product-card').length;
    el.textContent = el.textContent.replace(/\d+/, n);
  }

  function loadNext() {
    var url = nextUrl();
    if (!live || loading || !url) return;
    loading = true;
    setStatus('<span class="infinite-spinner" role="status" aria-label="' + BT_T('loading', 'Loading') + '"></span>');
    if (loadMoreBtn) loadMoreBtn.hidden = true;

    pending = window.AbortController ? new AbortController() : null;
    fetch(url, pending ? { signal: pending.signal } : {})
      .then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      })
      .then(function (text) {
        if (!current()) return;
        pending = null;
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var newGrid = doc.querySelector('[data-product-grid]');
        if (!newGrid) throw new Error('grid not found');

        // Move in the new page's product cards.
        var cards = newGrid.querySelectorAll('.product-card');
        var frag = document.createDocumentFragment();
        cards.forEach(function (card) { frag.appendChild(card); });
        grid.appendChild(frag);
        updateShowingCount();

        // Reflect the loaded page in the URL so returning from a product lands
        // the shopper where they were, not back on page 1.
        if (window.history && window.history.replaceState) {
          try { window.history.replaceState({}, '', url); } catch (e) {}
        }
        // Update or clear the next-page pointer.
        var newNext = newGrid.getAttribute('data-next-url');
        if (newNext) {
          grid.setAttribute('data-next-url', newNext);
          if (loadMoreBtn) loadMoreBtn.hidden = false;
          setStatus('');
        } else {
          grid.removeAttribute('data-next-url');
          if (loadMoreBtn) loadMoreBtn.remove();
          if (observer) observer.disconnect();
          setStatus('');
        }
        loading = false;
      })
      .catch(function () {
        // A request the swap called off, or one for a grid that has gone,
        // says nothing: the page on screen is not the one that asked.
        if (!current()) return;
        pending = null;
        loading = false;
        setStatus('Could not load more styles.');
        if (loadMoreBtn) loadMoreBtn.hidden = false;
      });
  }

  // A GRID THAT OPENS PAST PAGE 1 FILLS IN THE PAGES BEFORE IT (2026-10-07).
  // loadNext writes ?page=N into the address bar so a return from a product
  // lands near where the shopper was, but the server renders page N alone: a
  // reload, a bookmark, or a back navigation the browser does not serve from
  // its page cache showed that page's styles and never the ones above them
  // (the user, reloading Dresses at ?page=3: 12 of 60, and nothing loaded).
  // Pages 1 to N-1 are fetched and set in front, in order. A reload or a
  // return goes back to the position saved when the page was left; a page
  // the shopper has scrolled keeps the cards on screen where they are; a page
  // at the top (a bookmark, a shared link) simply starts at page 1.
  function pageUrl(n) {
    var u = new URL(window.location.href);
    if (n > 1) u.searchParams.set('page', n); else u.searchParams.delete('page');
    return u.pathname + u.search;
  }

  function scrollKey() {
    return 'bt-grid-scroll:' + window.location.pathname + window.location.search;
  }

  function savedScroll() {
    var nav = window.performance && performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
    if (!nav || (nav.type !== 'reload' && nav.type !== 'back_forward')) return null;
    try {
      var v = window.sessionStorage.getItem(scrollKey());
      return v === null ? null : parseInt(v, 10);
    } catch (e) { return null; }
  }

  function fillEarlier(upTo) {
    // Read now: a page loading below rewrites the address before this
    // finishes, and the position was saved under the address the page opened at.
    var restoreTo = savedScroll();
    var urls = [];
    for (var n = 1; n < upTo; n++) urls.push(pageUrl(n));
    filling = window.AbortController ? new AbortController() : null;
    var opts = filling ? { signal: filling.signal } : {};
    Promise.all(urls.map(function (u) {
      return fetch(u, opts).then(function (r) {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      });
    }))
      .then(function (texts) {
        if (!current()) return;
        filling = null;
        var frag = document.createDocumentFragment();
        texts.forEach(function (text) {
          var g = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-product-grid]');
          if (g) g.querySelectorAll('.product-card').forEach(function (card) { frag.appendChild(card); });
        });
        var anchor = grid.firstElementChild;
        var before = anchor ? anchor.getBoundingClientRect().top : 0;
        var scrolled = window.scrollY > 0;
        grid.insertBefore(frag, anchor);
        updateShowingCount();
        if (restoreTo !== null) window.scrollTo(0, restoreTo);
        // Measured, not assumed: a browser with scroll anchoring has already
        // held the anchor in place, and the difference is then zero.
        else if (scrolled && anchor) window.scrollBy(0, anchor.getBoundingClientRect().top - before);
      })
      .catch(function () {
        if (!current()) return;
        filling = null;
        setStatus('Could not load the styles before these.');
      });
  }

  var startPage = parseInt(grid.getAttribute('data-page'), 10) || 1;
  if (startPage > 1) fillEarlier(startPage);

  // The position to come back to, under the address the page leaves with
  // (the last page loaded). Bound once, like the scroll fallback below.
  if (!handleCollectionPagination.saveBound) {
    handleCollectionPagination.saveBound = true;
    window.addEventListener('pagehide', function () {
      if (!document.querySelector('[data-product-grid][data-page]')) return;
      try { window.sessionStorage.setItem(scrollKey(), String(Math.round(window.scrollY))); } catch (e) {}
    });
  }

  if (mode === 'load_more' && loadMoreBtn) {
    loadMoreBtn.addEventListener('click', loadNext);
  } else if (mode === 'infinite') {
    var sentinel = document.querySelector('[data-infinite-sentinel]');
    if (sentinel && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) loadNext();
      }, { rootMargin: '600px 0px' });
      observer.observe(sentinel);
    } else {
      // Fallback: a scroll listener. Bound once -- this function is re-run
      // after every filter swap, and a listener on window outlives the markup
      // that caused it -- and it loads through the loadNext of the latest
      // call, so it reads the grid on screen rather than the one it was bound
      // beside. The flag lives on the function: as a `var ... = false` after
      // ready()'s call it was reset behind the first call (see
      // handleCollectionsDropdown), so the first swap bound a second listener.
      handleCollectionPagination.loadNext = loadNext;
      if (!handleCollectionPagination.scrollBound) {
        handleCollectionPagination.scrollBound = true;
        window.addEventListener('scroll', function () {
          var load = handleCollectionPagination.loadNext;
          if (!load) return;
          var nearBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 600;
          if (nearBottom) load();
        });
      }
    }
  }
}

/*
 * MOBILE FILTER + SORT SHEET
 * Slide-up sheet containing sort radios and filter groups. Applying builds a
 * querystring from the form and navigates, so filters + sort submit together.
 */
function handleFilterSheet() {
  var sheet = document.querySelector('[data-sheet]');
  if (!sheet) return;

  var form = sheet.querySelector('[data-sheet-form]');
  var applyBtn = sheet.querySelector('[data-sheet-apply]');
  var openBtns = document.querySelectorAll('[data-sheet-open]');
  var closeEls = sheet.querySelectorAll('[data-sheet-close]');

  var panel = sheet.querySelector('.fsheet__panel') || sheet;

  // `hidden` stays the source of truth -- it is what the Escape handler and
  // the guards above read. `.is-open` rides along so the sheet answers the
  // same selector every other overlay does: the shared .bt-modal rules key on
  // .is-open, and `:not([hidden])` could not be shared, because the four
  // overlays that are never [hidden] would all have matched it permanently.
  function open() {
    if (!sheet.hidden) return;
    sheet.hidden = false;
    sheet.classList.add('is-open');
    window.BTModal.open(panel);
  }
  function close() {
    if (sheet.hidden) return;
    sheet.hidden = true;
    sheet.classList.remove('is-open');
    window.BTModal.close();
  }

  // Bound once per node: this runs again after a filter swap and a theme
  // editor reload, each with new nodes, and must not double up on old ones.
  openBtns.forEach(function (b) { if (b.__btSheetBound) return; b.__btSheetBound = true; b.addEventListener('click', open); });
  closeEls.forEach(function (b) { if (b.__btSheetBound) return; b.__btSheetBound = true; b.addEventListener('click', close); });
  // Bound once for the same reason as the pagination listener above: this runs
  // again after each filter swap, and a document listener survives the swap.
  // It closes the sheet on screen through that sheet's own close control, as
  // the width listener below does; `close` here belongs to the markup of the
  // call that bound it, which the first swap replaces. The once-only flags
  // live on the function, for the reason the pagination fallback gives.
  if (!handleFilterSheet.keyBound) {
    handleFilterSheet.keyBound = true;
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var live = document.querySelector('[data-sheet]');
      if (!live || live.hidden) return;
      var closer = live.querySelector('[data-sheet-close]');
      if (closer) closer.click();
    });
  }

  /* Crossing up to desktop has to close it.
   *
   * theme.css hides the sheet with `display: none` above 768px, which takes the
   * panel away but leaves it OPEN -- and BTModal's scroll lock with it. The
   * result is html stuck at `overflow: hidden` with the only control that could
   * release it invisible: the page will not scroll and there is nothing left to
   * click. Reported 2026-09-11, reproduced by opening the sheet at 390px and
   * widening the window.
   *
   * Bound once, for the reason the keydown above gives, and it reads the live
   * sheet rather than closing over `sheet`, because the markup is replaced on
   * every filter swap. Clicking the close control rather than hiding the node
   * runs whichever close() belongs to the markup on screen -- the same move the
   * swap itself makes. */
  if (!handleFilterSheet.mqBound && window.matchMedia) {
    handleFilterSheet.mqBound = true;
    var mq = window.matchMedia('(min-width: 769px)');
    var onCross = function (e) {
      if (!e.matches) return;
      var live = document.querySelector('[data-sheet]');
      if (!live || live.hidden) return;
      var closer = live.querySelector('[data-sheet-close]');
      if (closer) {
        closer.click();
      } else {
        live.hidden = true;
        live.classList.remove('is-open');
        if (window.BTModal) window.BTModal.close();
      }
    };
    if (mq.addEventListener) mq.addEventListener('change', onCross);
    else if (mq.addListener) mq.addListener(onCross);
  }

  /* The querystring the sheet currently describes, shared by `apply` and by the
     live count below so the two can never disagree about what is selected. */
  function sheetParams() {
    var params = new URLSearchParams();
    // Radios (sort) and checked checkboxes.
    form.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked').forEach(function (input) {
      params.append(input.name, input.value);
    });
    // If the sheet has no sort control of its own, preserve the active sort
    // from the URL so applying a filter doesn't drop it.
    if (!params.has('sort_by')) {
      var current = new URLSearchParams(window.location.search);
      if (current.get('sort_by')) params.set('sort_by', current.get('sort_by'));
    }
    // Price number inputs (only when filled).
    form.querySelectorAll('input[type="number"]').forEach(function (input) {
      if (input.value !== '') params.append(input.name, input.value);
    });
    // The in-stock constraint that used to be appended here is gone: size
    // filtering runs on custom.in_stock_sizes, which is already availability
    // aware. See the note where enforceInStockFilter used to live.
    return params;
  }

  function apply() {
    if (!form) return;
    var qs = sheetParams().toString();
    if (window.BTRememberScroll) window.BTRememberScroll();
    window.location.href = window.location.pathname + (qs ? '?' + qs : '');
  }

  /* "Show 50 results" counted what was on the page when the sheet was rendered,
     so it stood still while you ticked and only caught up after you pressed it
     -- the one number in the drawer that could not be trusted, on the control
     you press to commit. Client-reported 2026-09-11.
     
     The new label is READ BACK from the server's own render of the same button
     rather than computed here: the string is "Show {{ count }} results" through
     the locale file, and counting in JavaScript would mean reimplementing the
     pluralisation and the filter arithmetic, both of which Shopify has already
     done for this exact querystring.

     One request in flight at a time. Ticking three boxes quickly starts three,
     and without the abort the slowest could land last and write a stale
     number. */
  var countReq = null;
  var countTimer = null;

  function refreshCount() {
    if (!form || !applyBtn) return;
    var qs = sheetParams().toString();
    var url = window.location.pathname + (qs ? '?' + qs : '');
    if (countReq) countReq.abort();
    countReq = ('AbortController' in window) ? new AbortController() : null;
    applyBtn.setAttribute('aria-busy', 'true');
    fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: countReq ? countReq.signal : undefined
    })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var next = doc.querySelector('[data-sheet-apply]');
        if (next) applyBtn.textContent = next.textContent.trim();
        applyBtn.removeAttribute('aria-busy');
      })
      .catch(function (err) {
        // An abort is this function superseding itself, not a failure.
        if (!err || err.name !== 'AbortError') applyBtn.removeAttribute('aria-busy');
      });
  }

  if (form && applyBtn && !form.dataset.btCountBound) {
    form.dataset.btCountBound = '1';
    form.addEventListener('change', function () {
      clearTimeout(countTimer);
      countTimer = setTimeout(refreshCount, 220);
    });
    // Typing a price is not a change event until blur, so it gets the same
    // debounce off `input` -- a longer one, because it fires per keystroke.
    form.addEventListener('input', function (e) {
      if (e.target.type !== 'number') return;
      clearTimeout(countTimer);
      countTimer = setTimeout(refreshCount, 500);
    });
  }

  // Once per node, like the open and close controls above: rerun on the same
  // markup it added a second Apply each time (Codex, follow-up review 2).
  if (applyBtn && !applyBtn.__btSheetBound) { applyBtn.__btSheetBound = true; applyBtn.addEventListener('click', apply); }
}

/* ---------------------------------------------------------------------------
   Reviews hub: progressive reveal of the full review list.

   Every review is in the HTML already, written there by
   tools/sync-reviews.py --write-hub. This collapses the list AFTER load rather
   than the server sending a truncated one, so a reader without JS gets all of
   them and a crawler indexes all of them. The button starts [hidden] and is
   revealed here for the same reason: without JS it would do nothing.
--------------------------------------------------------------------------- */
(function () {
  var wrap = document.querySelector('[data-rv-all]');
  if (!wrap) return;
  var items = Array.prototype.slice.call(wrap.querySelectorAll('[data-rv-item]'));
  var section = wrap.closest('[data-rv-initial]');
  var step = parseInt((section && section.getAttribute('data-rv-initial')) || '12', 10);
  if (!step || step < 1) step = 12;
  if (items.length <= step) return;

  var btn = document.querySelector('[data-rv-more]');
  var shown = step;

  function paint() {
    items.forEach(function (el, i) { el.hidden = i >= shown; });
    if (!btn) return;
    var left = items.length - shown;
    btn.hidden = left <= 0;
    btn.textContent = left > 0
      ? BT_T('reviews_more_n', 'Show %d more of %t')
          .replace('%d', Math.min(step, left)).replace('%t', left)
      : BT_T('reviews_more', 'Show more reviews');
  }

  if (btn) {
    btn.hidden = false;
    btn.addEventListener('click', function () {
      var first = items[shown];
      shown = Math.min(shown + step, items.length);
      paint();
      // Move focus to the first newly revealed card so keyboard and screen
      // reader users land on the new content instead of the page bottom.
      if (first) {
        first.setAttribute('tabindex', '-1');
        first.focus({ preventScroll: true });
      }
    });
  }
  paint();
})();

/* ---------------------------------------------------------------------------
   Carousel arrows, for the phone-width product strips.

   Scrolls by one card rather than one viewport: the cards are 62% wide, so a
   viewport-sized jump would skip past one every time. scrollBy with smooth
   behaviour lets the browser land on its own snap point, which keeps the
   arrows and a swipe agreeing about where the strip stops.

   Everything is opt-in from the DOM: no arrows without JS, none when the track
   cannot scroll, and each one disables itself at its end of the strip. A
   control that scrolls nothing is worse than no control.

   Init is idempotent and re-runnable, watched from document.body, because the
   recommendations section replaces its own container wholesale after fetching
   -  `parentElement.innerHTML = ...` in product-recommendations.liquid. A
   listener bound on first load dies with the node it was bound to, and an
   observer watching the old track never fires again. That is exactly what
   happened: recently viewed worked and recommendations silently never showed
   its arrows.
--------------------------------------------------------------------------- */
(function () {
  /* Every live carousel, so one resize listener serves them all and a
     carousel whose markup was replaced (recommendations rewrite their own
     container) is let go of: its observers are disconnected when its wrap
     has left the document. */
  var live = [];
  function prune() {
    live = live.filter(function (c) {
      if (c.wrap.isConnected) return true;
      c.destroy();
      return false;
    });
  }
  window.addEventListener('resize', function () {
    prune();
    live.forEach(function (c) { c.update(); });
  });

  function initCarousel(wrap) {
    if (wrap.dataset.carouselReady === '1') return;
    var track = wrap.querySelector('[data-carousel-track]');
    var prev = wrap.querySelector('[data-carousel-prev]');
    var next = wrap.querySelector('[data-carousel-next]');
    if (!track || !prev || !next) return;
    wrap.dataset.carouselReady = '1';

    /* LOOPING IS THE SECTION'S CHOICE (the user, 2026-10-09: "make that
       optional ... a theme setting option for each module with a slideshow
       functionality"). `data-carousel-loop` on the wrap: past either end the
       strip carries on one slide at a time, so neither arrow is ever spent.
       Without it the strip stops at both ends, as below. A swipe still stops
       at the ends either way: it is the browser's scroll. */
    var loop = wrap.hasAttribute('data-carousel-loop');
    /* THE STRIP TAKES THE HEIGHT OF THE SLIDE ON SCREEN (2026-10-14, after
       Jeanne's "too much white space" under the Brinley). `data-carousel-fit`
       on the wrap: while the strip scrolls, its height is the slide in view,
       blended between two slides by how far the swipe has gone, so a short
       slide carries no white for a tall neighbour and what follows the strip
       moves with the finger. The slides must keep their own heights
       (align-items: flex-start in the strip). Off when the strip does not
       scroll, where the CSS lays the slides out side by side. */
    var fit = wrap.hasAttribute('data-carousel-fit'), lastFit = null;

    function scrolls() { return track.scrollWidth > track.clientWidth + 1; }

    function step() {
      var card = track.firstElementChild;
      var w = card ? card.getBoundingClientRect().width : track.clientWidth * 0.62;
      var gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
      return w + gap;
    }

    /* The arrows show only when there is something to scroll to, and each one
       says when it has nowhere left to go (the user, 2026-10-06: "when you reach
       the end and can no longer press in a direction the button should react by
       becoming half transparent"). aria-disabled rather than disabled, so a
       keyboard user who pressed Next to the end keeps focus on the button, and
       theme.css draws it at half opacity. Until then the strip looped, by moving
       the first card to the back at the end; that is gone. */
    function sync() {
      var on = scrolls();
      prev.hidden = !on;
      next.hidden = !on;
      prev.setAttribute('aria-disabled', on && (loop || !atStart()) ? 'false' : 'true');
      next.setAttribute('aria-disabled', on && (loop || !atEnd()) ? 'false' : 'true');
    }

    /* THE ARROWS SIT ON THE PICTURE'S MIDDLE (the user, 2026-10-06: "i want
       the arrows to vertically align based on the image, not the entire
       module"). The slide nearest the strip's centre lends its
       `[data-carousel-media]` box (its first image if it marks none), and its
       middle, measured from the carousel's top, becomes --carousel-nav-top.
       No media, no property, and the CSS falls back to 50%.

       Measured in one animation frame however many things ask, and written
       only when it changes. A ResizeObserver catches the picture growing as
       it loads and the strip changing width; scroll, image load, font load
       and a change of children ask too, because a box can move without
       resizing. */
    var raf = 0, lastTop = null, observed = null;
    function mediaOf() {
      var kids = track.children;
      if (!kids.length) return null;
      var tr = track.getBoundingClientRect();
      var cx = tr.left + tr.width / 2, best = null, bestD = Infinity;
      for (var i = 0; i < kids.length; i++) {
        var r = kids[i].getBoundingClientRect();
        if (!r.width) continue;
        var d = Math.abs(r.left + r.width / 2 - cx);
        if (d < bestD) { bestD = d; best = kids[i]; }
      }
      return best && (best.querySelector('[data-carousel-media]') || best.querySelector('img'));
    }
    var ro = window.ResizeObserver ? new ResizeObserver(function () { schedule(); }) : null;
    if (ro) { ro.observe(wrap); ro.observe(track); }
    if (ro && fit) Array.prototype.forEach.call(track.children, function (c) { ro.observe(c); });
    function fitHeight() {
      var kids = track.children, h = null;
      if (fit && scrolls() && kids.length) {
        /* Where each slide sits against the strip's padded edge, read off the
           screen rather than from scrollLeft, so the order of the children
           and the sign of an RTL scroll do not matter (Codex, REVIEW-2). */
        var cs = getComputedStyle(track);
        var padB = parseFloat(cs.paddingBottom) || 0;
        var edge = track.getBoundingClientRect().left + (parseFloat(cs.paddingLeft) || 0);
        var s = [];
        for (var i = 0; i < kids.length; i++) {
          var r = kids[i].getBoundingClientRect();
          if (r.width) s.push({ o: r.left - edge, h: r.height });
        }
        s.sort(function (a, b) { return a.o - b.o; });
        var j = 0;
        while (j < s.length - 1 && s[j + 1].o <= 0) j++;
        var k = Math.min(j + 1, s.length - 1);
        var t = k > j && s[j].o < 0 ? Math.min(1, -s[j].o / (s[k].o - s[j].o || 1)) : 0;
        h = Math.round(s[j].h + (s[k].h - s[j].h) * t + padB);
      }
      if (h === lastFit) return;
      lastFit = h;
      track.style.height = h === null ? '' : h + 'px';
    }
    function place() {
      raf = 0;
      fitHeight();
      var m = mediaOf();
      if (ro && m !== observed) {
        if (observed) ro.unobserve(observed);
        if (m) ro.observe(m);
        observed = m;
      }
      var top = null;
      if (m) {
        var mr = m.getBoundingClientRect();
        if (mr.height > 0) {
          var wr = wrap.getBoundingClientRect();
          top = Math.round(mr.top + mr.height / 2 - wr.top - wrap.clientTop);
        }
      }
      if (top === lastTop) return;
      lastTop = top;
      if (top === null) wrap.style.removeProperty('--carousel-nav-top');
      else wrap.style.setProperty('--carousel-nav-top', top + 'px');
    }
    function schedule() { if (!raf) raf = requestAnimationFrame(place); }
    function atEnd() {
      return track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
    }
    function atStart() { return track.scrollLeft <= 2; }

    var smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var how = { behavior: smooth ? 'smooth' : 'auto' };

    /* ONE SLIDE AT A TIME, ROUND THE END (the user, 2026-10-09: "on home page
       clicking left scrolls all the way to the left-most side of the
       collection, but it should just go one product to the left"). The loop
       first rewound across the whole strip. Now, at an end, the slide on the
       far side moves round to this one -- the first after the last, or the
       last before the first -- and the strip is put back by the width the
       move added or took away, in the same frame, so nothing shifts on
       screen; then it scrolls one step as it does anywhere else. The order of
       the slides changes; nothing is cloned. */
    function rotate(toEnd) {
      var kids = track.children;
      if (kids.length < 2) return;
      var ref = toEnd ? kids[1] : kids[0];
      var before = ref.offsetLeft, left = track.scrollLeft;
      if (toEnd) track.appendChild(kids[0]);
      else track.insertBefore(kids[kids.length - 1], kids[0]);
      track.scrollLeft = left + (ref.offsetLeft - before);
    }

    /* A LOOP ON A PHONE STARTS CENTRED (the user, 2026-10-09: "if loop is
       enabled the mobile version should start with the first product
       centered, and the part of the previous/next product showing to its
       left/right"). Where the stylesheet sets --carousel-centred: 1 (a
       looping product row under 701px, component-product-row.css) the slides
       snap to the middle, the last one stands before the first so there is a
       product to peek on the left, and the first is centred. Each time a
       scroll settles, balance() keeps a slide either side of the middle one,
       so a swipe goes round as the arrows do. Out of that layout (a wider
       window) the slides take their own order back, from the start. */
    var original = Array.prototype.slice.call(track.children);
    var wasCentred = false;
    function centredMode() {
      return loop && getComputedStyle(track).getPropertyValue('--carousel-centred').trim() === '1';
    }
    function middleIndex() {
      var tr = track.getBoundingClientRect(), cx = tr.left + tr.width / 2, best = -1, bestD = Infinity;
      for (var i = 0; i < track.children.length; i++) {
        var r = track.children[i].getBoundingClientRect();
        var d = Math.abs(r.left + r.width / 2 - cx);
        if (d < bestD) { bestD = d; best = i; }
      }
      return best;
    }
    function centreOn(el) {
      var tr = track.getBoundingClientRect(), r = el.getBoundingClientRect();
      track.scrollLeft += (r.left + r.width / 2) - (tr.left + tr.width / 2);
    }
    function balance() {
      if (!centredMode() || track.children.length < 3) return;
      var i = middleIndex();
      if (i === 0) rotate(false);
      else if (i === track.children.length - 1) rotate(true);
    }
    function layout() {
      var on = centredMode();
      if (on && !wasCentred) {
        original.forEach(function (c) { track.appendChild(c); });
        if (original.length >= 3) {
          track.insertBefore(track.lastElementChild, track.firstElementChild);
          centreOn(original[0]);
        }
      } else if (!on && wasCentred) {
        original.forEach(function (c) { track.appendChild(c); });
        track.scrollLeft = 0;
      }
      wasCentred = on;
    }
    if ('onscrollend' in window) {
      track.addEventListener('scrollend', balance);
    } else {
      var settle = 0;
      track.addEventListener('scroll', function () {
        clearTimeout(settle); settle = setTimeout(balance, 150);
      }, { passive: true });
    }

    prev.addEventListener('click', function () {
      if (!scrolls()) return;
      if (atStart()) {
        if (!loop) return;
        rotate(false);
      }
      track.scrollBy(Object.assign({ left: -step() }, how));
    });
    next.addEventListener('click', function () {
      if (!scrolls()) return;
      if (atEnd()) {
        if (!loop) return;
        rotate(true);
      }
      track.scrollBy(Object.assign({ left: step() }, how));
    });
    track.addEventListener('scroll', function () { sync(); schedule(); }, { passive: true });
    track.addEventListener('load', schedule, true);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);

    /* WHICH CARD IS THE MIDDLE ONE.

       The product strips show a sliver of each neighbour and hold those back
       to .45 opacity, so something has to say which card is centred. An
       observer rather than a scroll handler: rootMargin pinches the track's
       viewport to its middle 10%, so the only element that can intersect is
       the one under the centre line, and it fires on settle rather than on
       every frame of the scroll.

       `has-peek` is added here rather than sitting in the stylesheet, so a
       page whose JS has not run shows every card at full opacity instead of a
       whole strip dimmed with nothing to lift out of it. */
    function watchCentre() {
      if (!window.IntersectionObserver) return;
      if (track._peekObs) track._peekObs.disconnect();
      var obs = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          e.target.classList.toggle('is-current', e.isIntersecting);
        });
      }, { root: track, rootMargin: '0px -45% 0px -45%', threshold: 0 });
      Array.prototype.forEach.call(track.children, function (c) { obs.observe(c); });
      track._peekObs = obs;
      track.classList.add('has-peek');
    }

    /* Recently viewed fills its own track from localStorage after this runs,
       so watch the track too. */
    var mo = null;
    if (window.MutationObserver) {
      mo = new MutationObserver(function () {
        /* New slides (recently viewed arriving), as against the loop moving
           its own round: the set changes, so the centred start is set up
           again from them. */
        var kids = Array.prototype.slice.call(track.children);
        if (kids.length !== original.length || kids.some(function (c) { return original.indexOf(c) < 0; })) {
          original = kids; wasCentred = false; layout();
        }
        sync(); watchCentre(); schedule();
      });
      mo.observe(track, { childList: true });
    }
    live.push({
      wrap: wrap,
      update: function () { layout(); sync(); schedule(); },
      destroy: function () {
        if (ro) ro.disconnect();
        if (mo) mo.disconnect();
        if (track._peekObs) track._peekObs.disconnect();
        if (raf) cancelAnimationFrame(raf);
      }
    });
    layout();
    sync();
    watchCentre();
    schedule();
  }

  function scan() {
    prune();
    document.querySelectorAll('[data-carousel]').forEach(initCarousel);
  }

  scan();
  if (window.MutationObserver) {
    new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
  }
})();

/* A shorter search placeholder on a phone (the user, 2026-10-06): the header's
   "Search caftans, prints, colors…" was cut off in the popout's field at 390.
   search-bar.liquid carries both when a short one is set. */
(function () {
  var inputs = document.querySelectorAll('input[data-placeholder-short]');
  if (!inputs.length || !window.matchMedia) return;
  var mq = window.matchMedia('(max-width: 599px)');
  function apply() {
    Array.prototype.forEach.call(inputs, function (i) {
      i.placeholder = mq.matches ? i.getAttribute('data-placeholder-short') : i.getAttribute('data-placeholder-long');
    });
  }
  apply();
  if (mq.addEventListener) mq.addEventListener('change', apply);
  else if (mq.addListener) mq.addListener(apply);
})();

/* ---------------------------------------------------------------------------
   Size charts: let go of the pinned label column when it would hide the
   figures.

   The drawer's chart and the Size guide page's are one table (product.css),
   with the row labels sticky at the left so a wide chart can scroll under
   them. That only helps while the label leaves room: at 200% text a label
   measured 400px on a 288px scroller and no figure could be reached. The CSS
   releases it below 22em, which covers a larger browser font but not every
   kind of enlargement, so this measures instead (Codex, 2026-10-06): a first
   column wider than half its scroller is unpinned. Re-measured whenever the
   scroller or its table changes size (a ResizeObserver also fires when a
   closed drawer or a hidden chart tab is first shown), and once fonts load.
--------------------------------------------------------------------------- */
(function () {
  var scrollers = document.querySelectorAll('[data-chart-scroll]');
  if (!scrollers.length) return;
  function check(sc) {
    var th = sc.querySelector('table.chart tbody th, table.chart thead th');
    if (!th || !sc.clientWidth) return;
    sc.classList.toggle('is-unpinned', th.getBoundingClientRect().width > sc.clientWidth / 2);
  }
  var ro = window.ResizeObserver ? new ResizeObserver(function (entries) {
    entries.forEach(function (e) {
      var sc = e.target.closest('[data-chart-scroll]');
      if (sc) check(sc);
    });
  }) : null;
  Array.prototype.forEach.call(scrollers, function (sc) {
    if (ro) {
      ro.observe(sc);
      var t = sc.querySelector('table.chart');
      if (t) ro.observe(t);
    }
    check(sc);
  });
  window.addEventListener('resize', function () { Array.prototype.forEach.call(scrollers, check); });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () { Array.prototype.forEach.call(scrollers, check); });
  }
})();

/* ---- native <details> accordions slide --------------------------------
   The menu groups and both FAQs open over .3s; the filter-drawer groups, the
   store-locator regions and the cart note are native <details>, which snap.
   The client chose the slide for all of them (2026-10-01).

   They stay native so they still open with JS off. This takes over the
   summary's click (Enter and Space arrive as clicks too), animates the
   <details> box between its closed and open heights, and only then lets a
   closing one drop `open`, because content vanishes the moment it does.
   Delegated, because filtering swaps the filter sheet's markup.

   Height is read from the box as painted, so a second tap mid-slide turns it
   round from where it is rather than jumping. `.is-closing` turns the
   chevron back at the start of a close; `[open]` alone would hold it until
   the end. */
(function () {
  var SEL = '.fsheet__group, .bt-locator__group, .cart-note';
  var still = window.matchMedia('(prefers-reduced-motion: reduce)');

  document.addEventListener('click', function (e) {
    var s = e.target.closest('summary');
    var d = s && s.parentElement;
    if (!d || d.tagName !== 'DETAILS' || !d.matches(SEL)) return;
    if (still.matches || !d.animate) return;
    e.preventDefault();

    var from = d.getBoundingClientRect().height;
    if (d._slide) d._slide.cancel();
    var opening = !d.open || d.classList.contains('is-closing');
    var cs = getComputedStyle(d);
    var shut = s.getBoundingClientRect().height +
      parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom) +
      parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);

    d.classList.toggle('is-closing', !opening);
    d.open = true;
    var to = opening ? d.getBoundingClientRect().height : shut;

    d.style.overflow = 'hidden';
    var anim = d.animate({ height: [from + 'px', to + 'px'] },
                         { duration: 300, easing: 'ease' });
    d._slide = anim;
    anim.onfinish = function () {
      d._slide = null;
      d.style.overflow = '';
      if (!opening) d.open = false;
      d.classList.remove('is-closing');
    };
  });
})();

/* ---------------------------------------------------------------------------
   A QUICK EXTRA PRESS STILL BELONGS TO THE ARROW.

   A spent arrow (the strip is at that end) is invisible and takes no pointer,
   so a tap where it sat reaches what is under it: the photograph, the card,
   the lightbox's picture (theme.css, 2026-10-07). Pressing Next quickly to the
   end of the PDP gallery then opened the lightbox on the press after the last
   picture (the user: "I'd rather that bug be fixed"), and the same press would
   have opened a product from a phone's product row or closed the lightbox.

   So for 700ms after an arrow is pressed, a click inside that arrow's circle
   is the arrow's: it goes no further. Each one dropped restarts the window, so
   a run of presses stays with the arrow; a deliberate tap there afterwards
   reaches the picture. Capture phase on the document, so it runs before the
   gallery's own click handler, and one listener serves every .navbtn,
   including ones a section re-render puts in later.
--------------------------------------------------------------------------- */
(function () {
  if (window.__btArrowGrace) return;
  window.__btArrowGrace = true;
  var last = null, at = -Infinity;
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('.navbtn') : null;
    if (btn) { last = btn; at = e.timeStamp; return; }
    if (!last || !last.isConnected || e.timeStamp - at > 700) return;
    var r = last.getBoundingClientRect();
    if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
      e.preventDefault();
      e.stopPropagation();
      at = e.timeStamp;
    }
  }, true);
})();
